import React, {
  useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo
} from "react";
import { Send, XCircle, CheckCircle, ChevronDown, ArrowRight, Bot, PlugZap, Cpu, BarChart3, LineChart, Repeat2, Sparkles, BookOpen, Plus, PlayCircle, SlidersHorizontal, Trash2 } from "lucide-react";
import {
  API_ROOT,
  apiLogout,
  apiParseMessage,
  apiAgentWizardExecute,
  buildEACommand,
  apiAgentCommand,
  apiProfile,
  getToken,
} from "../api/api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import DOMPurify from "dompurify";

/* ---------- Thème ---------- */
function fmSetDark(isDark) { document.documentElement.classList.toggle("dark", !!isDark); }

/* ---------- Helpers dates & groupement de messages (requis par LS v2 et l'UI) ---------- */
const pad2 = (n) => String(n).padStart(2, "0");

function parseToDate(raw) {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;

  if (typeof raw === "number") {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const dt = new Date(ms);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  if (typeof raw === "string") {
    // "YYYY-MM-DD"
    const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      const dt = new Date(+ymd[1], +ymd[2] - 1, +ymd[3]);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
    // "DD/MM/YYYY" ou "DD/MM/YYYY HH:mm(:ss)?"
    const dmy = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (dmy) {
      const dt = new Date(
        +dmy[3],
        +dmy[2] - 1,
        +dmy[1],
        +(dmy[4] || 0),
        +(dmy[5] || 0),
        +(dmy[6] || 0)
      );
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
    const dt = new Date(raw);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

function getMessageDate(msg) {
  const raw =
    msg?.date ??
    msg?.createdAt ??
    msg?.created_at ??
    msg?.timestamp ??
    msg?.time ??
    msg?.ts ??
    null;
  return parseToDate(raw) || new Date();
}

const startOfDayLocal = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
const diffDaysLocal = (a, b) =>
  Math.round((startOfDayLocal(a) - startOfDayLocal(b)) / 86400000);

const dayKeyLocal = (value) => {
  const dt = value instanceof Date ? value : new Date(value);
  const base = Number.isNaN(dt.getTime()) ? new Date() : dt;
  return `${base.getFullYear()}-${pad2(base.getMonth() + 1)}-${pad2(base.getDate())}`;
};

function groupMessagesByDateLocal(messages) {
  const g = (messages || []).reduce((acc, m) => {
    const dt = getMessageDate(m);
    const k = dayKeyLocal(dt);
    (acc[k] ||= []).push({ ...m, __parsedDate: dt });
    return acc;
  }, {});
  Object.keys(g).forEach((k) => g[k].sort((a, b) => a.__parsedDate - b.__parsedDate));
  return g;
}

const capitalize = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

function formatDateChipFR(key, t) {
  const [y, m, d] = key.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date();
  const delta = diffDaysLocal(today, date);

  if (delta === 0) return t("chat.dates.today");
  if (delta === 1) return t("chat.dates.yesterday");
  if (delta > 1 && delta < 7)
    return capitalize(date.toLocaleDateString("fr-FR", { weekday: "long" }));

  const opts = {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() !== today.getFullYear() ? { year: "numeric" } : {}),
  };
  return date.toLocaleDateString("fr-FR", opts);
}

/* ---------- Brand & Utils ---------- */
const AI_NAME = "FULL METRIX";
const BRAND = {
  gradFrom: "from-violet-500",
  gradTo: "to-fuchsia-500",
  ring: "ring-violet-400/40",
  glow: "shadow-[0_18px_70px_rgba(124,58,237,0.18)]",
};
const cn = (...x) => x.filter(Boolean).join(" ");
const LONG_PRESS_MS = 500;
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
const normalize = (s = "") => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().replace(/[^\p{L}\p{N}\s/_\-().,]+/gu, " ");
const genCRID = () => (crypto?.randomUUID?.() || `crid_${Date.now()}_${Math.floor(Math.random() * 1e6)}`);
const WELCOME_GREETING_TYPE = "welcome_greeting_panel";
const WELCOME_CONNECTION_TYPE = "welcome_connection_panel";
const WELCOME_ACTIONS_TYPE = "welcome_actions_panel";

// Message préenregistré « Comment ça marche ? » (FR/EN). Quand l'utilisateur
// l'envoie, on répond localement par une explication scriptée (aucun backend).
// Les clés sont normalisées (sans accents/ponctuation, espaces compactés).
const HOWTO_PRESETS = new Set([
  "comment ca marche",
  "how does it work",
  "how it works",
  "how does this work",
]);
const howtoKeyOf = (s = "") => normalize(s).replace(/\s+/g, " ").trim();


function cleanViewerName(value = "") {
  return String(value || "")
    .trim()
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .split(" ")[0]
    .trim();
}

function pickUserDisplayName(payload = {}) {
  const source = payload?.user || payload?.profile || payload?.data || payload || {};
  const candidates = [
    source.first_name,
    source.firstname,
    source.firstName,
    source.display_name,
    source.displayName,
    source.username,
    source.name,
    source.full_name,
    source.fullName,
    source.email ? String(source.email).split("@")[0] : "",
  ];

  const raw = candidates.find((value) => typeof value === "string" && value.trim());
  const cleaned = cleanViewerName(raw || "");
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : "";
}

function formatDisplayName(value = "") {
  const cleaned = cleanViewerName(value || "");
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : "";
}

function getScopedKey(baseKey, scope = "anonymous") {
  const safeScope = String(scope || "anonymous").replace(/[^a-zA-Z0-9_-]/g, "_") || "anonymous";
  return `${baseKey}:${safeScope}`;
}

function readScopedValue(baseKey, scope = "anonymous") {
  try {
    const key = getScopedKey(baseKey, scope);
    return (window.localStorage.getItem(key) || window.sessionStorage.getItem(key) || "").trim();
  } catch {
    return "";
  }
}

function resolveStoredViewerName(scope = null) {
  try {
    const safeScope = scope || (typeof getCurrentChatScope === "function" ? getCurrentChatScope() : "anonymous");

    // ✅ Ne jamais relire directement fm_user_display_name global.
    // Cette clé est legacy et provoque exactement le bug: ancien prénom affiché après changement de compte.
    const scopedDirect = formatDisplayName(readScopedValue("fm_user_display_name", safeScope));
    if (scopedDirect) return scopedDirect;

    const bridgeScope = window.sessionStorage.getItem("fm:user:display_name:current_scope") || "";
    const bridgeName = window.sessionStorage.getItem("fm:user:display_name:current") || "";
    if (bridgeName && bridgeScope === safeScope) return formatDisplayName(bridgeName);

    const legacyOwner = window.localStorage.getItem("fm_user_display_name_owner_scope") || "";
    if (legacyOwner && legacyOwner === safeScope) {
      const legacyOwned = formatDisplayName(window.localStorage.getItem("fm_user_display_name") || "");
      if (legacyOwned) return legacyOwned;
    }

    // Fallback uniquement sur des profils scopés. Jamais sur user/auth_user globaux.
    const storageKeys = [
      "user",
      "auth_user",
      "currentUser",
      "fm_user",
      "fullmetrix_user",
      "fullmargin_user",
      "profile",
      "sso_user",
      "user_data",
      "authData",
    ];

    for (const key of storageKeys) {
      const raw = readScopedValue(key, safeScope);
      if (!raw) continue;
      const parsed = safeParse(raw, raw);
      const found = pickUserDisplayName(parsed);
      if (found) return found;
    }
  } catch {}
  return "";
}

function rememberViewerNameForScope(name, scope = null) {
  const finalName = formatDisplayName(name);
  if (!finalName) return "";

  try {
    const safeScope = scope || (typeof getCurrentChatScope === "function" ? getCurrentChatScope() : "anonymous");
    const key = getScopedKey("fm_user_display_name", safeScope);

    window.localStorage.setItem(key, finalName);
    window.sessionStorage.setItem(key, finalName);
    window.sessionStorage.setItem("fm:user:display_name:current", finalName);
    window.sessionStorage.setItem("fm:user:display_name:current_scope", safeScope);

    // ✅ On neutralise la clé globale. Les autres pages doivent utiliser la clé scopée.
    window.localStorage.removeItem("fm_user_display_name");
    window.localStorage.setItem("fm_user_display_name_owner_scope", safeScope);
  } catch {}

  return finalName;
}

function clearViewerNameCache(scope = null) {
  try {
    const safeScope = scope || (typeof getCurrentChatScope === "function" ? getCurrentChatScope() : "anonymous");
    window.localStorage.removeItem(getScopedKey("fm_user_display_name", safeScope));
    window.sessionStorage.removeItem(getScopedKey("fm_user_display_name", safeScope));

    if ((window.sessionStorage.getItem("fm:user:display_name:current_scope") || "") === safeScope) {
      window.sessionStorage.removeItem("fm:user:display_name:current");
      window.sessionStorage.removeItem("fm:user:display_name:current_scope");
    }

    if (!scope || (window.localStorage.getItem("fm_user_display_name_owner_scope") || "") === safeScope) {
      window.localStorage.removeItem("fm_user_display_name");
      window.localStorage.removeItem("fm_user_display_name_owner_scope");
    }
  } catch {}
}


// Salutation selon l'heure : « Bon après-midi, Ngouan ». Plus chaleureux qu'un
// libellé fixe, et ça donne le ton d'une app qui te reconnaît.
function timeGreeting(date = new Date(), t) {
  const h = date.getHours();
  if (h >= 5 && h < 12) return t("chat.greeting.morning");
  if (h >= 12 && h < 18) return t("chat.greeting.afternoon");
  return t("chat.greeting.evening");
}

// DEV uniquement : sans backend local aucun profil ne remonte, donc le prénom
// reste vide et on ne voit jamais le vrai rendu de la salutation. On en tire un
// au hasard, une fois par chargement de page (nouveau nom à chaque actualisation).
// `process.env.NODE_ENV` est inliné par le build : en production la constante
// vaut "" et le tableau est éliminé — ça ne peut pas fuiter chez l'utilisateur.
const DEV_FAKE_NAMES = ["Emmanuel", "Ngouan", "Sarah", "Koffi", "Lucas", "Amina", "Thomas", "Fatou"];
const DEV_VIEWER_NAME =
  process.env.NODE_ENV === "development"
    ? DEV_FAKE_NAMES[Math.floor(Math.random() * DEV_FAKE_NAMES.length)]
    : "";

// Salutation d'accueil, en morceaux : « Bon après-midi » + « Ngouan » (le prénom
// est déjà isolé et capitalisé par cleanViewerName). Séparés pour que le rendu
// puisse passer le prénom à la ligne sur mobile sans le faire sur desktop.
function heroGreetingParts(name = "", date = new Date(), t) {
  return { greeting: timeGreeting(date, t), who: name || DEV_VIEWER_NAME };
}

function buildWelcomeGreetingMeta(name = "", t) {
  const base = timeGreeting(new Date(), t);
  const greeting = name
    ? t("chat.welcome.greeting.withName", { greeting: base, name })
    : t("chat.welcome.greeting.plain", { greeting: base });
  return {
    greeting,
    intro: t("chat.welcome.greeting.intro"),
    badge: t("chat.welcome.greeting.badge"),
  };
}

function buildWelcomeConnectionMeta(t) {
  return {
    title: t("chat.welcome.connection.title"),
    text: t("chat.welcome.connection.text"),
  };
}

function buildWelcomeActionsMeta(t) {
  return {
    title: t("chat.welcome.actions.title"),
    items: [
      t("chat.welcome.actions.items.performance"),
      t("chat.welcome.actions.items.journal"),
      t("chat.welcome.actions.items.launch"),
      t("chat.welcome.actions.items.copy"),
    ],
    ctaLabel: t("chat.welcome.actions.ctaLabel"),
    ctaTarget: "/agent",
  };
}

const WELCOME_ICON_MAP = {
  analyse: BarChart3,
  insights: LineChart,
  execution: PlugZap,
  copy: Repeat2,
};

function WelcomeAvatarEmoji() {
  return (
    <div className="hidden sm:grid h-8 w-8 shrink-0 place-items-center rounded-[12px] border border-white/35 bg-gradient-to-br from-violet-500 via-fuchsia-500 to-sky-500 text-[16px] shadow-[0_10px_22px_rgba(124,58,237,0.18)] ring-1 ring-white/25 dark:border-white/10 sm:h-9 sm:w-9">
      <span aria-hidden>🤖</span>
    </div>
  );
}

function WelcomeBubble({ children, glassClass = "", className = "" }) {
  return (
    <div
      className={cn(
        "relative min-w-0 flex-1 overflow-hidden",
        // mobile : texte brut, aucune carte
        "rounded-none border-0 bg-transparent px-0 py-0",
        // desktop : carte vitrée
        "sm:rounded-[18px] sm:border sm:border-white/60 sm:bg-white/78 sm:px-4 sm:py-3",
        "sm:shadow-[0_12px_28px_rgba(15,18,32,0.06)] sm:dark:border-white/10 sm:dark:bg-[linear-gradient(135deg,rgba(18,22,42,0.95)_0%,rgba(13,18,34,0.92)_55%,rgba(10,16,30,0.94)_100%)] sm:dark:shadow-[0_20px_44px_rgba(0,0,0,0.30)]",
        className
      )}
    >
      <div className="pointer-events-none absolute inset-0 hidden sm:block bg-[radial-gradient(circle_at_top_left,rgba(139,92,246,0.09),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(96,165,250,0.05),transparent_24%)] dark:bg-[radial-gradient(circle_at_top_left,rgba(139,92,246,0.18),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_28%)]" />
      <div className="relative">{children}</div>
    </div>
  );
}

function WelcomeGreetingCard({ meta = {}, glassClass = "" }) {
  const { t } = useTranslation();
  return (
    <div className="w-full max-w-[470px]">
      <div className="flex items-start gap-2.5">
        <WelcomeAvatarEmoji />
        <WelcomeBubble glassClass={glassClass} className="max-w-[426px]">
          <div className="text-[0.98rem] font-semibold tracking-[-0.03em] text-slate-950 dark:text-white sm:text-[1.03rem]">
            {meta.greeting || t("chat.welcome.greeting.fallback")}
          </div>

          <p className="mt-1.5 text-[13px] leading-6.5 text-slate-700/88 dark:text-slate-200">
            {meta.intro || t("chat.welcome.greeting.intro")}
          </p>

          
        </WelcomeBubble>
      </div>
    </div>
  );
}

function WelcomeConnectionCard({ meta = {}, glassClass = "" }) {
  const { t } = useTranslation();
  const capabilities = Array.isArray(meta.capabilities) ? meta.capabilities : [];

  return (
    <div className="w-full max-w-[470px] pl-0 sm:pl-[40px]">
      <WelcomeBubble glassClass={glassClass} className="max-w-[426px]">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[13px] border border-violet-200 bg-violet-50/85 text-violet-700 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-200">
            <Cpu size={17} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-[0.95rem] font-semibold tracking-[-0.02em] text-slate-950 dark:text-white">
              {meta.title || t("chat.welcome.connection.title")}
            </div>

            <p className="mt-1.5 text-[13.5px] leading-6.5 text-slate-700/88 dark:text-slate-200">
              {meta.text || t("chat.welcome.connection.text")}
            </p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-1.5">
          {capabilities.map((item, index) => {
            const Icon = WELCOME_ICON_MAP[item.icon] || PlugZap;
            return (
              <div
                key={`${item.icon || "feature"}-${index}`}
                className="flex min-h-[50px] items-start gap-2 rounded-[14px] border border-white/55 bg-white/76 px-3 py-2 shadow-[0_8px_18px_rgba(15,18,32,0.03)] dark:border-white/10 dark:bg-white/[0.06] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]"
              >
                <div className="grid h-7 w-7 shrink-0 place-items-center rounded-[10px] border border-white/55 bg-white/88 text-violet-700 dark:border-white/12 dark:bg-white/10 dark:text-violet-100">
                  <Icon size={15} />
                </div>
                <div className="min-w-0 text-[12px] font-medium leading-5.5 text-slate-700 dark:text-slate-100">
                  {item.label}
                </div>
              </div>
            );
          })}
        </div>
      </WelcomeBubble>
    </div>
  );
}

function WelcomeActionsCard({ meta = {}, onOpenAgent, glassClass = "" }) {
  const { t } = useTranslation();
  const items = Array.isArray(meta.items) ? meta.items : [];

  return (
    <div className="w-full max-w-[470px] pl-0 sm:pl-[40px]">
      <WelcomeBubble glassClass={glassClass} className="max-w-[426px]">
        <div className="text-[0.95rem] font-semibold tracking-[-0.02em] text-slate-950 dark:text-white">
          {meta.title || t("chat.welcome.actions.title")}
        </div>

        <div className="mt-2.5 grid grid-cols-1 gap-x-3 gap-y-0.5 min-[540px]:grid-cols-2">
          {items.map((item, index) => (
            <div key={`${item}-${index}`} className="flex items-start gap-2 py-0.5">
              <div className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-gradient-to-r from-violet-500 to-sky-500 shadow-[0_0_0_4px_rgba(139,92,246,0.08)] dark:shadow-[0_0_0_4px_rgba(139,92,246,0.14)]" />
              <div className="text-[13.5px] leading-6.5 text-slate-700/90 dark:text-slate-200">
                {item}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-2.5">
          <button
            type="button"
            onClick={onOpenAgent}
            className={cn(
              "group relative inline-flex w-full items-center justify-between gap-3 overflow-hidden rounded-[15px] px-4 py-2.5 text-left text-white sm:w-auto sm:min-w-[252px]",
              "bg-[linear-gradient(135deg,#2b1767_0%,#6d28d9_48%,#c026d3_100%)] shadow-[0_12px_28px_rgba(109,40,217,0.26)]",
              "transition duration-150 hover:-translate-y-[1px] hover:shadow-[0_18px_36px_rgba(109,40,217,0.30)] active:translate-y-0 active:scale-[0.995]"
            )}
          >
            <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.14),transparent)]" />
            <span className="relative min-w-0 flex-1">
              <span className="block text-[9.5px] font-semibold uppercase tracking-[0.15em] text-white/78">
                {t("chat.welcome.actions.ctaKicker")}
              </span>
              <span className="mt-0.5 block text-[14px] font-semibold tracking-[-0.02em] text-white">
                {meta.ctaLabel || t("chat.welcome.actions.ctaLabel")}
              </span>
            </span>

            <span className="relative grid h-8 w-8 shrink-0 place-items-center rounded-[12px] border border-white/20 bg-white/10 text-white transition group-hover:bg-white/16">
              <ArrowRight size={17} />
            </span>
          </button>
        </div>
      </WelcomeBubble>
    </div>
  );
}

// Conserve "." et "," pour éviter 0.01 -> 001
const normalizeForNumbers = (s = "") =>
  s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/* ============================================================
   SYMBOL NORMALIZATION (UNIVERSEL + DERIV SAFE)
   ============================================================ */

/** Nettoie sans casser les symboles qui contiennent espaces/parenthèses/suffixes */
function cleanSymbolRaw(s = "") {
  return String(s || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/^[=:]+/g, "")
    .replace(/[;]+$/g, "")
    .trim();
}

/** Détecte si le symbole ressemble à un nom MT5 "long" (espaces/parenthèses) -> garder la casse */
function isLongNamedSymbol(sym = "") {
  const s = String(sym);
  return /[()]/.test(s) || /\s/.test(s);
}

/** Alias Deriv/Synthetics -> forme canonique (casse exacte attendue le plus souvent sur Deriv MT5) */
const DERIV_ALIASES = [
  // Volatility 50 1s / Volatility 50 (1s) / vol 50 s / vol 50 1 s
  { re: /\b(?:vol|volatility)\s*(\d{1,3})\s*(?:\(?\s*1\s*s\)?|1\s*s|1s|s)\b/i, make: (n) => `Volatility ${n} (1s) Index` },
  // V50 / V75 / V100 + variantes 1s (Deriv synthetics)
  { re: /\bV\s*(\d{1,3})\s*(?:\(?\s*1\s*s\)?|1\s*s|1s)\b/i, make: (n) => `Volatility ${n} (1s) Index` },
  { re: /\bV\s*(\d{1,3})\b/i, make: (n) => `Volatility ${n} Index` },
  // Volatility 50
  { re: /\b(?:vol|volatility)\s*(\d{1,3})\b/i, make: (n) => `Volatility ${n} Index` },
  // Boom/Crash
  { re: /\bboom\s*(\d{3,4})\b/i, make: (n) => `Boom ${n} Index` },
  { re: /\bcrash\s*(\d{3,4})\b/i, make: (n) => `Crash ${n} Index` },
  // Jump
  { re: /\bjump\s*(\d{2,3})\b/i, make: (n) => `Jump ${n} Index` },
  // Step Index (souvent: Step Index)
  { re: /\bstep\s*index\b/i, make: () => `Step Index` },
  // Range Break 100/200 etc.
  { re: /\brange\s*break\s*(\d{2,3})\b/i, make: (n) => `Range Break ${n} Index` },
];

/** Indices/alias usuels (⚠️ broker dependent : ce sont des "best-effort") */
const COMMON_SYMBOL_ALIASES = [
  // Metals
  { re: /\b(xauusd|gold|or)\b/i, out: "XAUUSD" },
  { re: /\b(xagusd|silver|argent)\b/i, out: "XAGUSD" },

  // Indices (Weltrade / codes CFD courants)
  { re: /\b(us30|dow\s*jones|dow|dji|dj30|wall\s*street\s*30)\b/i, out: "US30" },
  { re: /\b(us500|spx500|sp\s*500|s&p\s*500|sp500)\b/i, out: "US500" },
  { re: /\b(nas100|nas\s*100|nasdaq\s*100|us\s*tech\s*100)\b/i, out: "NAS100" },
  { re: /\b(ger40|dax\s*40?|de40)\b/i, out: "GER40" },
  { re: /\b(fra40|cac\s*40?|fr40)\b/i, out: "FRA40" },
  { re: /\b(uk100|ftse\s*100?|gb100)\b/i, out: "UK100" },
  { re: /\b(jpn225|nikkei\s*225?|jp225)\b/i, out: "JPN225" },
  { re: /\b(hk50|hang\s*seng)\b/i, out: "HK50" },
  { re: /\b(aus200|asx\s*200|au200)\b/i, out: "AUS200" },
  { re: /\b(estx50|euro\s*stoxx\s*50|eurostoxx\s*50)\b/i, out: "ESTX50" },

  // Indices Synthétiques (SyntX) — Série FX Vol & SFX (volatilité fixe)
  { re: /\bfx\s*vol\s*(\d{1,3})\b/i, make: (n) => `FX Vol ${n}` },
  { re: /\bsfx\s*vol\s*(\d{1,3})\b/i, make: (n) => `SFX Vol ${n}` },

  // Série QuadX (croissance quadratique)
  { re: /\bquadx\s*(100|200|300|400)\b/i, make: (n) => `QuadX ${n}` },

  // Série PainX (tendance haussière / crashs)
  { re: /\bpainx\s*(400|600|800|1200)\b/i, make: (n) => `PainX ${n}` },

  // Série GainX (tendance baissière / sauts)
  { re: /\bgainx\s*(400|600|800|1200)\b/i, make: (n) => `GainX ${n}` },
];

/** Normalise Forex style: EUR/USD, eurusd, EURUSD -> EURUSD */
function normalizeForexPair(text = "") {
  const s = String(text).toUpperCase().replace(/\s+/g, "");
  // EUR/USD
  const m = s.match(/\b([A-Z]{3})\/([A-Z]{3})\b/);
  if (m) return `${m[1]}${m[2]}`;
  // EURUSD
  const m2 = s.match(/\b([A-Z]{6})\b/);
  if (m2) return m2[1];
  return "";
}

/** Normalise Crypto pair: BTC/USD, BTCUSDT, ETHUSD -> garder uppercase */
function normalizeCryptoPair(text = "") {
  const s = String(text).toUpperCase().replace(/\s+/g, "");
  const m = s.match(/\b([A-Z]{2,6})\/([A-Z]{3,5})\b/);
  if (m) return `${m[1]}${m[2]}`;
  // BTCUSD, ETHUSDT
  const m2 = s.match(/\b([A-Z]{5,10})\b/);
  if (m2) {
    const cand = m2[1];
    if (/(BTC|ETH|SOL|XRP|BNB|ADA|DOGE)/.test(cand)) return cand;
  }
  return "";
}

/** Canonicalise Deriv symbol from free text */
function canonicalizeDerivSymbolFromText(text = "") {
  const t = cleanSymbolRaw(text);
  if (!t) return "";

  // Important: ne jamais faire matcher "FX Vol xx" / "SFX Vol xx"
  // avec les symboles Deriv "Volatility xx Index".
  if (/\b(?:fx|sfx)\s*vol\s*\d{1,3}\b/i.test(t)) return "";

  for (const a of DERIV_ALIASES) {
    const m = t.match(a.re);
    if (m) return typeof a.make === "function" ? a.make(m[1]) : "";
  }
  return "";
}

/** Canonicalise a single symbol input (universal, best-effort) */
function canonicalizeSymbol(input = "") {
  const raw = cleanSymbolRaw(input);
  if (!raw) return "";

  // 0) Weltrade SyntX: FX Vol / SFX Vol (prioritaire sur Deriv "Volatility")
  const wfx = raw.match(/\bfx\s*vol\s*(\d{1,3})\b/i);
  if (wfx) return `FX Vol ${wfx[1]}`;
  const wsfx = raw.match(/\bsfx\s*vol\s*(\d{1,3})\b/i);
  if (wsfx) return `SFX Vol ${wsfx[1]}`;


  // 1) Deriv synthetics (preserve exact canonical casing/spaces)
  const d = canonicalizeDerivSymbolFromText(raw);
  if (d) return d;

  // 2) Codes avec suffixes "broker" (souvent sensibles à la casse) :
  //    EURUSDm, EURUSD.m, US30.cash, NAS100.m, etc.
  //    => on les préserve AVANT les alias communs pour respecter le symbole exact tapé.
  const compact = String(raw).replace(/\s+/g, "");
  const fxSuf = compact.match(/^([A-Za-z]{6})([A-Za-z0-9._\-]{1,10})$/);
  if (fxSuf) return `${fxSuf[1].toUpperCase()}${fxSuf[2]}`;

  const idxSuf = compact.match(/^([A-Za-z]{2,6}\d{0,4})([._\-][A-Za-z0-9]{1,12})$/);
  if (idxSuf) return `${idxSuf[1].toUpperCase()}${idxSuf[2]}`;

  // 3) Common aliases (metals, indices, synthetics words)
  for (const a of COMMON_SYMBOL_ALIASES) {
    const m = raw.match(a.re);
    if (m) return typeof a.make === "function" ? a.make(m[1]) : a.out;
  }

  // 4) Forex pairs (EUR/USD, EURUSD)
  const fx = normalizeForexPair(raw);
  if (fx) return fx;

  // 5) Crypto pairs (BTC/USD, BTCUSDT, ETHUSD...)
  const cx = normalizeCryptoPair(raw);
  if (cx) return cx;

  // 6) If it looks like a long named symbol (spaces/parenthesis) => keep as-is (don’t uppercase)
  if (isLongNamedSymbol(raw)) return raw;

  // 7) Default: uppercase compact codes
  return raw.toUpperCase();
}

/** Safe keepSymbol used by extractors */
function keepSymbol(s = "") {
  return canonicalizeSymbol(s);
}

/* ---------- Local storage mirror (v2, robuste pour les ordres) ---------- */
const LOCAL_KEY = "fm:chat:local_v2";
const LOCAL_MIRRORED_TYPES = new Set([
  "text",
  "cta",
  "order_receipt",
  "user_command",
  "user_navigation",
]);
const CHAT_SCOPED_STORAGE_BASES = [
  LOCAL_KEY,
  "agent_intent_v1",
  "nav_intent_v1",
  "agent_token",
  "fm_user_display_name",
];
const AUTH_STORAGE_KEYS_FOR_CHAT = [
  "token", "access_token", "accessToken", "jwt", "jwt_token",
  "authToken", "AUTH_TOKEN", "fm_token",
  "user", "auth_user", "currentUser", "fm_user", "fullmetrix_user",
  "fullmargin_user", "profile", "sso_user", "user_data", "authData",
];

function safeParse(json, fallback) {
  try {
    const v = JSON.parse(json);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

let ACTIVE_CHAT_SCOPE = "anonymous";

function sanitizeScope(scope = "anonymous") {
  return String(scope || "anonymous").replace(/[^a-zA-Z0-9_-]/g, "_") || "anonymous";
}

function getUserIdentityValue(payload = {}) {
  const source = payload?.user || payload?.profile || payload?.data || payload || {};
  const candidates = [
    source.id,
    source.user_id,
    source.userId,
    source.fullmargin_id,
    source.fullmarginId,
    source.sub,
    source.email,
    source.username,
  ];

  const found = candidates.find((value) => value !== null && value !== undefined && String(value).trim());
  return found ? String(found).trim() : "";
}

function hashString(value = "") {
  const str = String(value || "");
  let hash = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function makeUserScope(identity = "") {
  const raw = String(identity || "").trim().toLowerCase();
  return raw ? `user_${hashString(raw)}` : "anonymous";
}

function decodeJwtPayload(token = "") {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const json = decodeURIComponent(
      Array.from(atob(padded))
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getTokenCandidatesForScope() {
  const out = [];

  try {
    if (typeof getToken === "function") {
      const value = getToken();
      if (value && String(value).trim()) out.push(String(value).trim());
    }
  } catch {}

  const keys = [
    "token", "access_token", "accessToken", "access", "jwt", "jwt_token",
    "authToken", "AUTH_TOKEN", "auth_token", "fm_token", "fullmetrix_token",
    "fullmargin_token", "access_token_fullmetrix"
  ];

  try {
    for (const key of keys) {
      const value = window.localStorage.getItem(key) || window.sessionStorage.getItem(key);
      if (value && String(value).trim()) out.push(String(value).trim());
    }
  } catch {}

  return Array.from(new Set(out));
}

function rememberAuthTokenForScope(token, scope = null) {
  const value = String(token || "").trim();
  if (!value) return "";

  try {
    const safeScope = sanitizeScope(scope || getCurrentChatScope() || "anonymous");
    window.sessionStorage.setItem("fm:auth_token:current", value);
    window.sessionStorage.setItem("fm:auth_token:current_scope", safeScope);
    window.sessionStorage.setItem(scopedStorageKey("fm_auth_token", safeScope), value);
    window.localStorage.setItem(scopedStorageKey("fm_auth_token", safeScope), value);
  } catch {}

  return value;
}

function getRememberedAuthTokenForScope(scope = getCurrentChatScope()) {
  try {
    const safeScope = sanitizeScope(scope || "anonymous");

    const bridgeScope = window.sessionStorage.getItem("fm:auth_token:current_scope") || "";
    const bridgeToken = window.sessionStorage.getItem("fm:auth_token:current") || "";
    if (bridgeToken && bridgeScope === safeScope) return bridgeToken.trim();

    const scopedKey = scopedStorageKey("fm_auth_token", safeScope);
    const scoped = window.sessionStorage.getItem(scopedKey) || window.localStorage.getItem(scopedKey) || "";
    if (scoped && String(scoped).trim()) return String(scoped).trim();
  } catch {}

  return "";
}

function clearRememberedAuthTokenForScope(scope = null) {
  try {
    const safeScope = sanitizeScope(scope || getCurrentChatScope() || "anonymous");
    window.sessionStorage.removeItem(scopedStorageKey("fm_auth_token", safeScope));
    window.localStorage.removeItem(scopedStorageKey("fm_auth_token", safeScope));

    if ((window.sessionStorage.getItem("fm:auth_token:current_scope") || "") === safeScope) {
      window.sessionStorage.removeItem("fm:auth_token:current");
      window.sessionStorage.removeItem("fm:auth_token:current_scope");
    }
  } catch {}
}

function resolveStoredSessionProfile() {
  try {
    // ✅ Priorité au token de session, jamais aux profils globaux potentiellement anciens.
    for (const token of getTokenCandidatesForScope()) {
      const payload = decodeJwtPayload(token);
      if (getUserIdentityValue(payload)) return payload;
    }

    const currentScope = (() => {
      try { return window.sessionStorage.getItem("fm:chat:active_scope") || ""; } catch { return ""; }
    })();

    // Fallback seulement si le profil porte explicitement le scope courant.
    const storageKeys = [
      "user", "auth_user", "currentUser", "fm_user", "fullmetrix_user",
      "fullmargin_user", "profile", "sso_user", "user_data", "authData"
    ];

    for (const key of storageKeys) {
      const raw = currentScope
        ? (window.localStorage.getItem(getScopedKey(key, currentScope)) || window.sessionStorage.getItem(getScopedKey(key, currentScope)))
        : "";
      if (!raw) continue;
      const parsed = safeParse(raw, raw);
      if (getUserIdentityValue(parsed)) return parsed;
    }
  } catch {}
  return null;
}

function resolveStoredChatScope() {
  const profile = resolveStoredSessionProfile();
  const identity = getUserIdentityValue(profile);
  if (identity) return makeUserScope(identity);

  const token = getTokenCandidatesForScope()[0] || "";
  return token ? makeUserScope(token) : "anonymous";
}

function setActiveChatScope(scope = resolveStoredChatScope()) {
  ACTIVE_CHAT_SCOPE = sanitizeScope(scope || "anonymous");
  try { window.sessionStorage.setItem("fm:chat:active_scope", ACTIVE_CHAT_SCOPE); } catch {}
  return ACTIVE_CHAT_SCOPE;
}

function getCurrentChatScope() {
  try {
    const stored = window.sessionStorage.getItem("fm:chat:active_scope");
    if (stored) return sanitizeScope(stored);
  } catch {}
  return ACTIVE_CHAT_SCOPE || setActiveChatScope(resolveStoredChatScope());
}

function scopedStorageKey(baseKey, scope = getCurrentChatScope()) {
  return `${baseKey}:${sanitizeScope(scope)}`;
}

setActiveChatScope("anonymous");

function toISODate(d) {
  const dt = getMessageDate({ date: d });
  return (dt instanceof Date && !Number.isNaN(dt.getTime()))
    ? dt.toISOString()
    : new Date().toISOString();
}

function summarizeOrderPayload(p) {
  if (!p || typeof p !== "object") return "";
  const {
    cmd, command, order_type, kind,
    symbol, side, volume, lot, lot_size, price, sl, tp, id, ticket
  } = p;
  const vol = (Number.isFinite(volume) ? volume
    : Number.isFinite(lot_size) ? lot_size
      : Number.isFinite(lot) ? lot : "");
  const c = (cmd || command || order_type || kind || "").toString().toLowerCase();

  // IMPORTANT: symbole canonicalisé sans casser les suffixes (ex: US30.cash) ni les noms Deriv longs
  const sym = symbol ? keepSymbol(String(symbol)) : "";

  return [
    c, sym,
    (side || "").toString().toLowerCase(),
    (vol ?? "").toString(),
    (price ?? "").toString(),
    (sl ?? "").toString(),
    (tp ?? "").toString(),
    (id ?? ticket ?? "").toString()
  ].join("|");
}

function fingerprint(msg) {
  const author = msg?.author || "ai";
  const type = msg?.type || "text";
  const crid = msg?.meta?.client_request_id || msg?.meta?.crid || msg?.crid || "";

  // IMPORTANT : le même client_request_id peut être utilisé pour :
  // 1) le message utilisateur "user_command" ;
  // 2) le reçu IA "order_receipt".
  // On garde donc author + type dans la clé pour éviter que le payload écrase
  // le texte exact envoyé par l'utilisateur après reload.
  if (crid) return `crid:${author}|${type}|${crid}`;

  const pid = msg?.persistedId || "";
  if (pid) return `pid:${pid}`;

  if (type === "order_receipt") {
    const sig = summarizeOrderPayload(msg?.meta?.payload || {});
    const ts = toISODate(msg?.date || new Date());
    return `ord:${sig}|${ts}`;
  }

  const ts = toISODate(msg?.date || new Date());
  const head = (msg?.text || "").slice(0, 256);
  return `msg:${author}|${type}|${ts}|${head}`;
}

function readScopedPayload(key) {
  const raw = localStorage.getItem(key);
  if (!raw) return [];
  const parsed = safeParse(raw, []);
  if (Array.isArray(parsed)) return parsed;
  if (parsed && Array.isArray(parsed.items)) return parsed.items;
  return [];
}

function readLocal(scope = getCurrentChatScope()) {
  try {
    return readScopedPayload(scopedStorageKey(LOCAL_KEY, scope));
  } catch {
    return [];
  }
}

function writeLocal(arr, scope = getCurrentChatScope()) {
  try {
    const safeScope = sanitizeScope(scope);
    const items = (Array.isArray(arr) ? arr : []).slice(-1000);
    const payload = { v: 3, scope: safeScope, items };
    localStorage.setItem(scopedStorageKey(LOCAL_KEY, safeScope), JSON.stringify(payload));
  } catch { }
}

function sanitizeForStore(m) {
  const dateISO = toISODate(m?.date || new Date());
  const base = {
    author: m?.author || "ai",
    text: m?.text || "",
    date: dateISO,
    type: m?.type || "text",
    meta: m?.meta && typeof m.meta === "object" ? { ...m.meta } : {},
    persistedId: m?.persistedId || null
  };
  const crid = base.meta?.client_request_id || base.meta?.crid || null;
  if (crid) base.crid = crid;

  base._fp = fingerprint({ ...base });

  try {
    if (base.type === "order_receipt" && base.meta?.payload) {
      base.meta._summary = summarizeOrderPayload(base.meta.payload);
    }
  } catch { }
  return base;
}

function addLocalMessage(m, scope = getCurrentChatScope()) {
  const safeScope = sanitizeScope(scope);
  const list = readLocal(safeScope);
  const entry = sanitizeForStore(m);

  const dupIndex = list.findIndex(x => {
    const sameCrid =
      entry.crid && x.crid && x.crid === entry.crid &&
      (x.author || "ai") === (entry.author || "ai") &&
      (x.type || "text") === (entry.type || "text");

    const samePersisted =
      entry.persistedId && x.persistedId &&
      String(x.persistedId) === String(entry.persistedId);

    const sameFingerprint = x._fp && entry._fp && x._fp === entry._fp;

    return sameCrid || samePersisted || sameFingerprint;
  });

  if (dupIndex >= 0) {
    const prev = list[dupIndex] || {};
    list[dupIndex] = {
      ...prev,
      ...entry,
      id: prev.id || entry.id || null,
      persistedId: entry.persistedId || prev.persistedId || null,
      crid: entry.crid || prev.crid || null,
      meta: { ...(prev.meta || {}), ...(entry.meta || {}) },
      _fp: entry._fp || prev._fp,
    };
    writeLocal(list, safeScope);
    return;
  }

  if (entry.type === "order_receipt" && entry.meta?._summary) {
    const ts = Date.parse(entry.date) || Date.now();
    const nearIndex = list.slice(-50).findIndex(x => {
      if (x.type !== "order_receipt") return false;
      if (!x.meta?._summary) return false;
      const dx = Math.abs((Date.parse(x.date) || 0) - ts);
      return x.meta._summary === entry.meta._summary && dx <= 1200;
    });
    if (nearIndex >= 0) return;
  }

  list.push(entry);
  writeLocal(list, safeScope);
}

function persistLocalFallbackMessage(message, scope = getCurrentChatScope()) {
  mirrorMessageLocally(message, scope);
}

function removeLocalByCRID(crid) {
  if (!crid) return;
  const list = readLocal().filter(x => x.crid !== crid);
  writeLocal(list);
}

function removeLocalByPersistedId(pid) {
  if (!pid) return;
  const list = readLocal().filter(x => x.persistedId !== pid);
  writeLocal(list);
}

function removeLocalByFP(finger) {
  if (!finger) return;
  const list = readLocal().filter(x => x._fp !== finger);
  writeLocal(list);
}

const fp = (m) => sanitizeForStore(m)._fp;

function removeStorageItemSafe(storage, key) {
  try { storage?.removeItem(key); } catch {}
}

function removeLegacyJsonIfOwned(key, scope) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return;
    const parsed = safeParse(raw, null);
    if (!parsed || parsed.owner_scope === scope || parsed.scope === scope) {
      localStorage.removeItem(key);
    }
  } catch {}
}

function clearChatLocalForScope(scope = getCurrentChatScope()) {
  const safeScope = sanitizeScope(scope || "anonymous");

  try {
    CHAT_SCOPED_STORAGE_BASES.forEach((base) => {
      removeStorageItemSafe(localStorage, scopedStorageKey(base, safeScope));
      removeStorageItemSafe(sessionStorage, scopedStorageKey(base, safeScope));
    });

    removeStorageItemSafe(sessionStorage, "fm:chat:active_scope");
    removeStorageItemSafe(sessionStorage, "fm:agent_token:current");
    removeStorageItemSafe(sessionStorage, "fm:agent_token:current_scope");

    removeLegacyJsonIfOwned("agent_intent_v1", safeScope);
    removeLegacyJsonIfOwned("nav_intent_v1", safeScope);

    if ((localStorage.getItem("agent_token_owner_scope") || "") === safeScope) {
      removeStorageItemSafe(localStorage, "agent_token");
      removeStorageItemSafe(localStorage, "agent_token_owner_scope");
    }

    if ((localStorage.getItem("agent_accounts_owner_scope") || "") === safeScope) {
      removeStorageItemSafe(localStorage, "agent_accounts_v3");
      removeStorageItemSafe(localStorage, "agent_active_idx");
      removeStorageItemSafe(localStorage, "agent_accounts_owner_scope");
    }

    clearViewerNameCache(safeScope);
    clearRememberedAuthTokenForScope(safeScope);
  } catch {}

  if (ACTIVE_CHAT_SCOPE === safeScope) ACTIVE_CHAT_SCOPE = "anonymous";
}

function clearLocalMessageHistoryForScope(scope = getCurrentChatScope()) {
  const safeScope = sanitizeScope(scope || "anonymous");
  try {
    removeStorageItemSafe(localStorage, scopedStorageKey(LOCAL_KEY, safeScope));
    removeStorageItemSafe(sessionStorage, scopedStorageKey(LOCAL_KEY, safeScope));
  } catch {}
}

function mirrorMessageLocally(message, scope = getCurrentChatScope()) {
  try {
    if (!LOCAL_MIRRORED_TYPES.has(message?.type || "text")) return;
    addLocalMessage(message, scope);
  } catch {}
}

/* ---------- Markdown ---------- */
marked.setOptions({ gfm: true, breaks: true });

function MarkdownRenderer({ content }) {
  const clean = useMemo(() => {
    const s = typeof content === "string" ? content : String(content ?? "");
    return DOMPurify.sanitize(marked.parse(s));
  }, [content]);

  return (
    <div
      className={cn(
        "break-words whitespace-pre-wrap text-slate-900 dark:text-slate-50",
        "[&>p]:mb-2 [&>p:last-child]:mb-0",
        "[&>ul]:ml-5 [&>ol]:ml-5 [&>li]:list-disc [&_ol>li]:list-decimal",
        "[&>h1]:text-lg [&>h2]:text-base [&>h3]:text-sm [&>h1]:mt-2 [&>h2]:mt-2 [&>h3]:mt-2",
        "[&_strong]:font-semibold [&_strong]:text-slate-950 dark:[&_strong]:text-white",
        "[&_em]:text-slate-700 dark:[&_em]:text-slate-200",
        "[&_a]:font-medium [&_a]:text-violet-700 [&_a]:underline [&_a]:underline-offset-4 dark:[&_a]:text-violet-300",
        "[&_code]:rounded [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:bg-black/[0.05] [&_code]:text-slate-900 dark:[&_code]:bg-white/[0.08] dark:[&_code]:text-slate-100",
        "[&_pre]:p-3 [&_pre]:rounded-xl [&_pre]:overflow-x-auto [&_pre]:text-sm [&_pre]:bg-slate-950 [&_pre]:text-slate-100 dark:[&_pre]:bg-[#050816] dark:[&_pre]:text-slate-100",
        "[&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:text-slate-700 dark:[&_blockquote]:border-white/15 dark:[&_blockquote]:text-slate-200"
      )}
      style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

/* ---------- Parsing léger local (brouillon multi-tours) ---------- */

/* ---------- Symbol stopwords (évite de prendre BUY/SELL comme symbole) ---------- */
const SYMBOL_STOPWORDS = new Set([
  "buy","sell","long","short","achat","acheter","achet","vente","vendre","close","cloture","cloturer","fermer","ferme","fermeture",
  "tout","toute","toutes","all","position","positions","les","la","le","de","des","du","au","aux","un","une",
  "modify","modif","modifier","change","changer","ajuste","ajuster","update","set",
  "market","limit","stop","pending","order","ordre",
  "tp","sl","be","breakeven","break","even","trailing","trail",
  "risk","rr","entry","price","at","@" 
]);

function isSymbolStopword(tok="") {
  const t = String(tok||"").trim().toLowerCase();
  return SYMBOL_STOPWORDS.has(t);
}

function guessSymbolFromFreeText(s = "") {
  const raw = String(s || "");

  // 0) label explicite "symbol: XXX" / "instrument: XXX"
  const label = raw.match(/(?:symbol|instrument)\s*[:=]\s*([A-Za-z0-9 _\-()./]{3,})/i)?.[1];
  if (label) return keepSymbol(label);

  // 0-bis) Si l'utilisateur tape directement un symbole après buy/sell,
  //        on essaie d'abord ce segment exact pour respecter le symbole saisi.
  const afterSide = raw.match(/\b(?:buy|sell|long|short|achat|acheter|achet|vente|vendre)\b\s+(.+?)(?=(?:\s+(?:avec|with)\b|\s+\d+(?:[.,]\d+)?\s*(?:lot|lots|volume|size)\b|\s+(?:lot|lots|volume|size)\b|\s+(?:sl|tp|stop\s*loss|take\s*profit|entry|price)\b|$))/i)?.[1]?.trim();
  if (afterSide) {
    const exact = keepSymbol(afterSide);
    if (exact && !isSymbolStopword(exact)) return exact;
  }

  // 1) Alias connus PRIORITAIRES sur le texte complet.
  //    Important pour FX Vol / SFX Vol / Range Break / QuadX / PainX / GainX.
  for (const a of COMMON_SYMBOL_ALIASES) {
    const m = raw.match(a.re);
    if (m) return keepSymbol(typeof a.make === "function" ? a.make(m[1]) : a.out);
  }

  // 2) Reconnaitre explicitement les patterns synthétiques / indices multi-mots.
  //    On met FX VOL / SFX VOL avant VOL/Vxx pour éviter toute confusion avec Deriv.
  const synt = raw.match(/\b(FX\s*VOL\s*\d+|SFX\s*VOL\s*\d+|RANGE\s*BREAK\s*\d+|STEP\s*INDEX|QUADX\s*\d+|PAINX\s*\d+|GAINX\s*\d+|VOL\s*\d+\s*(?:\(?\s*1\s*s\)?|1\s*s|1s|s)?|V\s*\d+\s*(?:\(?\s*1\s*s\)?|1\s*s|1s)?|V\d+|BOOM\s*\d+|CRASH\s*\d+|JUMP\s*\d+)\b/ig);
  if (synt?.length) return keepSymbol(synt[0]);

  // 3) "sur XXX"
  const sur = raw.match(/\bsur\s+([A-Za-z0-9 _\-()./]{3,})/i)?.[1];
  if (sur) return keepSymbol(sur);

  // 4) Deriv ensuite seulement
  const deriv = canonicalizeDerivSymbolFromText(raw);
  if (deriv) return keepSymbol(deriv);

  // 5) Forex/crypto with slash
  const pair = raw.match(/\b([A-Za-z]{2,6})\s*\/\s*([A-Za-z]{3,5})\b/);
  if (pair) return keepSymbol(`${pair[1]}${pair[2]}`);

  // 6) Compact token codes (EURUSD, BTCUSD, US30, NAS100...)
  const compact = raw.match(/\b([A-Za-z]{3,8}\d{0,4}(?:[._][A-Za-z0-9]{1,12})?)\b/g);
  if (compact?.length) {
    const cand =
      compact.find(x => /[A-Za-z]/.test(x) && x.length >= 3 && !isSymbolStopword(x)) ||
      compact.find(x => /[A-Za-z]/.test(x) && x.length >= 3) ||
      compact[0];
    return keepSymbol(cand);
  }

  return "";
}

function guessSideFrom(text = "") {
  const s = ` ${normalize(text)} `;
  if (/\bach(?:a|e)?t\w*\b/.test(s) || /\bachat\b/.test(s) || /\bbuy\b/.test(s) || /\blong\b/.test(s)) return "buy";
  if (/\bvend\w*\b/.test(s) || /\bvente\b/.test(s) || /\bvendre\b/.test(s) || /\bsell\b/.test(s) || /\bshort\b/.test(s)) return "sell";
  return "";
}

function guessLotFrom(text = "") {
  const s = normalizeForNumbers(text);

  const m1 = s.match(/\blot(?:\s*de)?\s*([0-9]+(?:[.,][0-9]+)?)\b/i)?.[1];
  const m2 = s.match(/\b([0-9]+(?:[.,][0-9]+)?)\s*(?:lot|lots|volume|size)\b/i)?.[1];
  const m3 = s.match(/\b(?:lot|lots|volume|size)\s*[:=]\s*([0-9]+(?:[.,][0-9]+)?)\b/i)?.[1];

  let raw = (m1 || m2 || m3 || "").replace(/\s/g, "");

  // Heuristique (optionnelle): "buy EURUSD 0.05" (sans mot 'lot')
  // -> on ne l'utilise que si l'utilisateur donne un ordre (buy/sell/achat/vente)
  //    et que la valeur finale ressemble à un volume (<= 100).
  if (!raw) {
    const hasSide = /\b(buy|sell|long|short|achat|vente|vendre|acheter)\b/i.test(s);
    if (hasSide) {
      const stripped = s
        .replace(/(?:\bsl\b|\btp\b|\bstop\s*loss\b|\btake\s*profit\b|\bprice\b|\bentry\b|@)\s*[:=]?\s*[0-9]+(?:[.,][0-9]+)?/ig, " ")
        .replace(/\s+/g, " ")
        .trim();
      const tail = stripped.match(/(?:^|\s)([0-9]+(?:[.,][0-9]+)?)\s*$/)?.[1];
      if (tail) raw = tail.replace(/\s/g, "");
    }
  }

  if (!raw) return undefined;

  const n = parseFloat(raw.replace(",", "."));
  if (!Number.isFinite(n)) return undefined;

  const MIN = 0.01;
  const MAX = 100;

  // Si c'est un énorme nombre, c'est probablement un prix, pas un lot.
  if (n > MAX) return undefined;

  const safe = Math.min(Math.max(n, MIN), MAX);
  return Number(safe.toFixed(2));
}

function extractPartialFromText(text = "") {
  return {
    symbol: guessSymbolFromFreeText(text) || "",
    side: guessSideFrom(text) || "",
    lot_size: guessLotFrom(text)
  };
}

function mergeDraft(prev, next) {
  const out = { ...(prev || {}) };
  if (next.symbol) out.symbol = next.symbol;
  if (typeof next.lot_size === "number" && Number.isFinite(next.lot_size)) out.lot_size = next.lot_size;
  if (next.side) out.side = next.side;
  return out;
}

function requiresSymbol(cmd) {
  if (!cmd || !cmd.cmd) return false;
  const c = String(cmd.cmd).toLowerCase();
  const need = new Set(["market", "pending", "modify", "close", "breakeven", "trailing"]);
  if (!need.has(c)) return false;
  return !cmd.symbol;
}


function isSimpleOpenMarketText(text = "") {
  const s = ` ${normalize(text)} `;
  const hasSide = /\b(buy|sell|long|short|achat|vente|vendre|acheter|achet)\b/i.test(s);
  if (!hasSide) return false;

  // On exclut les modifications / fermetures / pending / SL/TP etc.
  const bad = [
    /\bclose(?:\s*all)?\b|\bclotur\w*\b|\bfermer?\b/i,
    /\bmodif\w*\b|\bchange\w*\b|\bajust\w*\b|\bmove\b|\bupdate\b|\bset\b/i,
    /\bpending\b|\blimit\b|\bstop\b/i,
    /\btp\b|\bsl\b|\btake\s*profit\b|\bstop\s*loss\b|\btrailing\b|\bbreakeven\b|\bbreak\s*even\b/i
  ];
  if (bad.some(re => re.test(s))) return false;

  return true;
}

function synthesizeSimpleOpenMarket(text = "", draft = {}) {
  if (!isSimpleOpenMarketText(text)) return null;

  const side = (guessSideFrom(text) || draft?.side || "").toLowerCase();
  const symbolRaw = guessSymbolFromFreeText(text) || draft?.symbol || "";
  const symbol = keepSymbol(canonicalizeSymbol(symbolRaw) || symbolRaw);
  const lot = (guessLotFrom(text) ?? draft?.lot_size);

  if (!side || !symbol) return null;

  const cmd = { cmd: "market", side, symbol };
  if (typeof lot === "number" && Number.isFinite(lot)) {
    cmd.volume = lot;
    cmd.lot_size = lot;
    cmd.lot = lot;
  }
  return cmd;
}

function trySynthesizeCmd(text, draft = {}) {
  const part = extractPartialFromText(text);
  const side = (part.side || draft.side || "").toLowerCase();
  const symbol = keepSymbol(part.symbol || draft.symbol || "");
  const volume = (part.lot_size ?? draft.lot_size);

  if (!side || !symbol) return null;
  const cmd = { cmd: "market", side, symbol };
  if (Number.isFinite(volume)) cmd.volume = volume;
  return cmd;
}

/* ---------- Intent trading & Navigation ---------- */
function isTradingText(t = "") {
  const s = ` ${normalize(t)} `;
  const patterns = [
    /\bach(?:a|e)?t\w*\b/,
    /\bvend\w*\b|\bvente\b/,
    /\bbuy\b|\blong\b|\bsell\b|\bshort\b/,
    /\bclose(?:\s*all)?\b|\bclotur\w*|\bfermer?\b/,
    /\bmodif\w*|\bchange\w*|\bajust\w*|\bdeplac\w*|\bmove\b/,
    /\btp\b|\btake\s*profit\b|\bsl\b|\bstop\s*loss\b|\bbreakeven\b|\bbreak\s*even\b|\btrailing\b|\btrail\w*\b/,
    /\bordre\b|\border\b|\bpending\b|\blimit\b|\bstop\b/,
    /\bposition\b|\bpositions\b/
  ];
  return patterns.some(re => re.test(s));
}

function isTradingIntent(parsed, userText) {
  try {
    const p = parsed?.order || parsed?.data || parsed || {};
    const intent = String(p.intent || p.type || p.action || p.domain || "").toLowerCase();
    const side = String(p.side || "").toLowerCase();
    const order = String(p.cmd || p.kind || p.order_type || "").toLowerCase();
    const flags = [
      intent.includes("trade") || intent.includes("order") || intent.includes("mt5"),
      ["buy", "sell", "close", "modify", "pending", "breakeven", "trailing"].some(k => intent.includes(k)),
      ["buy", "sell"].includes(side),
      /(buy|sell)_(limit|stop)/.test(order)
    ];
    if (flags.some(Boolean)) return true;
  } catch { }
  if (isTradingText(userText)) return true;
  return false;
}

function detectNavIntent(text = "", t) {
  const norm = normalize(text);
  const hasGo = /\b(ouvre|ouvrir|open|lance|lancer|va|vas|allons|aller|acc[eè]de|accede|acc[eè]der|passe|dirige|redirige|am[eè]ne|envoie|go\s*to|vers)\b/.test(norm);
  const agentHit = /\b(agent\s*ia|\/agent|page\s*agent|agent)\b/.test(norm);
  const backtestHit = /\b(backtest|back\s*test|replay|\/backtest)\b/.test(norm);
  const strategiesHit = /\b(strategie|strategies|strategie?s|strat[eé]gies|\/strateg)/.test(norm);
  if (agentHit && (hasGo || true)) return { target: "/agent", title: t("chat.nav.agent.title"), reason: t("chat.nav.agent.reason") };
  if (backtestHit && (hasGo || true)) return { target: "/backtest", title: t("chat.nav.backtest.title"), reason: t("chat.nav.backtest.reason") };
  if (strategiesHit && (hasGo || true)) return { target: "/strategies", title: t("chat.nav.strategies.title"), reason: t("chat.nav.strategies.reason") };
  return null;
}

/* ---------- JSON intercept helpers ---------- */
function extractFenceJSON(s = "") {
  const trimmed = String(s || "").trim();
  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence && fence[1]) return fence[1].trim();
  if (/^\{[\s\S]*\}$/.test(trimmed) || /^\[[\s\S]*\]$/.test(trimmed)) return trimmed;
  return null;
}
function tryParseJSONBlocks(s = "") {
  const body = extractFenceJSON(s);
  if (!body) return null;
  try { return JSON.parse(body); } catch { return null; }
}
function looksLikeAgentPlan(obj) {
  if (!obj || typeof obj !== "object") return false;
  const keys = Object.keys(obj).map(k => k.toLowerCase());
  if (["cmd", "command", "order", "symbol", "intent"].some(k => keys.includes(k))) return true;
  if (obj.order && typeof obj.order === "object") return true;
  return false;
}


/* ---------- API auth helpers ChatPage ---------- */
function getBearerTokenForChat(scope = getCurrentChatScope()) {
  if (typeof window === "undefined") return "";

  const safeScope = sanitizeScope(scope || "anonymous");

  // Priorité absolue au token validé pendant la résolution du compte (/api/me ou apiProfile).
  // Cela évite d'envoyer à /api/chat un vieux token global encore présent dans localStorage.
  const rememberedToken = getRememberedAuthTokenForScope(safeScope);
  if (rememberedToken) return rememberedToken;

  const candidates = getTokenCandidatesForScope();

  for (const token of candidates) {
    try {
      if (!token || !String(token).trim()) continue;
      const value = String(token).trim();
      const payload = decodeJwtPayload(value);
      const identity = getUserIdentityValue(payload);

      // IMPORTANT : au démarrage, le scope peut être "anonymous" avant /api/me.
      // Dans ce cas, on doit quand même envoyer le Bearer token, sinon /api/history
      // et /api/me répondent 401 et le prénom ne peut jamais être récupéré.
      if (!identity || safeScope === "anonymous" || makeUserScope(identity) === safeScope) {
        return value;
      }
    } catch {}
  }

  const userKeys = [
    "user",
    "auth_user",
    "currentUser",
    "fm_user",
    "fullmetrix_user",
    "fullmargin_user",
    "profile",
    "sso_user",
    "user_data",
    "authData",
  ];

  for (const key of userKeys) {
    try {
      const raw =
        window.localStorage.getItem(getScopedKey(key, safeScope)) ||
        window.sessionStorage.getItem(getScopedKey(key, safeScope));
      if (!raw) continue;
      const parsed = safeParse(raw, null);
      const value =
        parsed?.token ||
        parsed?.access_token ||
        parsed?.accessToken ||
        parsed?.jwt ||
        parsed?.authToken ||
        parsed?.data?.token ||
        parsed?.user?.token ||
        "";
      if (value && String(value).trim()) return String(value).trim();
    } catch {}
  }

  return "";
}
function authHeadersForChat(extra = {}) {
  const headers = { Accept: "application/json", ...extra };
  const token = getBearerTokenForChat();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function fetchJsonForChat(path, options = {}) {
  const headers = {
    ...authHeadersForChat(),
    ...(options.headers || {}),
  };

  // Chemin relatif ("/api/…") → il viserait le proxy CRA du package.json en
  // développement, pas l'API configurée. On le résout sur API_ROOT ; une URL
  // absolue passe telle quelle.
  const resolved = /^https?:\/\//i.test(path)
    ? path
    : `${API_ROOT}${String(path).replace(/^\/api/, "")}`;

  const doFetch = async (nextHeaders) => {
    const response = await fetch(resolved, {
      credentials: "include",
      ...options,
      headers: nextHeaders,
    });
    const data = await response.json().catch(() => ({}));
    return { response, data };
  };

  let { response, data } = await doFetch(headers);

  // Si un vieux Bearer global bloque la session cookie valide, on réessaie sans Authorization.
  // C'est fréquent après déconnexion/reconnexion sur le même navigateur.
  if ((response.status === 401 || response.status === 403) && headers.Authorization) {
    const retryHeaders = { ...headers };
    delete retryHeaders.Authorization;
    const retry = await doFetch(retryHeaders);
    if (retry.response.ok) return retry.data;

    // On garde la vraie erreur du retry seulement si elle est plus informative.
    response = retry.response;
    data = retry.data;
  }

  if (!response.ok) {
    return {
      error: data?.error || data?.message || `HTTP ${response.status}`,
      status: response.status,
      payload: data,
    };
  }
  return data;
}

async function fetchHistoryForChat() {
  return fetchJsonForChat("/api/history", { method: "GET" });
}

async function sendChatForChat(message) {
  return fetchJsonForChat("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message }),
  });
}

const MESSAGE_DUPLICATE_WINDOW_MS = 2500;
function messageDedupeKey(m = {}) {
  const type = m.type || "text";
  const author = m.author || "ai";
  const crid = m?.meta?.client_request_id || m?.meta?.crid || m?.crid || "";
  const persistedId = m.persistedId || m.id || "";

  // Ne jamais dédupliquer un user_command avec un order_receipt :
  // ils peuvent partager le même client_request_id mais ce sont deux messages différents.
  if (crid) return `${author}|${type}|crid:${crid}`;
  if (persistedId && typeof persistedId !== "number") return `${author}|${type}|pid:${persistedId}`;

  const text = String(m.text || "").trim().replace(/\s+/g, " ").slice(0, 500);
  const payloadSummary = type === "order_receipt" ? summarizeOrderPayload(m?.meta?.payload || {}) : "";
  return `${author}|${type}|${text}|${payloadSummary}`;
}

function dedupeMessages(list = []) {
  const out = [];
  const seen = new Map();

  for (const msg of Array.isArray(list) ? list : []) {
    const key = messageDedupeKey(msg);
    const t = getMessageDate(msg).getTime();
    const previousIndex = seen.get(key);

    if (previousIndex !== undefined) {
      const prev = out[previousIndex];
      const prevT = getMessageDate(prev).getTime();
      const closeInTime = Math.abs(t - prevT) <= MESSAGE_DUPLICATE_WINDOW_MS;
      const samePersisted = msg.persistedId && prev.persistedId && String(msg.persistedId) === String(prev.persistedId);
      const msgCrid = msg?.meta?.client_request_id || msg?.meta?.crid || msg?.crid || "";
      const prevCrid = prev?.meta?.client_request_id || prev?.meta?.crid || prev?.crid || "";
      const sameCrid =
        msgCrid && prevCrid && msgCrid === prevCrid &&
        (msg.author || "ai") === (prev.author || "ai") &&
        (msg.type || "text") === (prev.type || "text");

      if (closeInTime || samePersisted || sameCrid) {
        out[previousIndex] = {
          ...prev,
          ...msg,
          id: prev.id || msg.id,
          persistedId: prev.persistedId || msg.persistedId,
          meta: { ...(prev.meta || {}), ...(msg.meta || {}) },
        };
        continue;
      }
    }

    seen.set(key, out.length);
    out.push(msg);
  }

  return out.sort((a, b) => getMessageDate(a) - getMessageDate(b));
}

/* ---------- Agent token (séparé du SSO) ---------- */
function readStorageValue(area, key) {
  try {
    if (typeof window === "undefined") return "";
    return (window[area]?.getItem(key) || "").trim();
  } catch {
    return "";
  }
}

function rememberAgentTokenForChat(token, scope = getCurrentChatScope()) {
  const value = String(token || "").trim();
  const safeScope = sanitizeScope(scope);
  if (!value) return;

  try {
    localStorage.setItem(scopedStorageKey("agent_token", safeScope), value);
    localStorage.setItem("agent_token", value);
    localStorage.setItem("agent_token_owner_scope", safeScope);
    sessionStorage.setItem(scopedStorageKey("agent_token", safeScope), value);
    sessionStorage.setItem("fm:agent_token:current", value);
    sessionStorage.setItem("fm:agent_token:current_scope", safeScope);
  } catch {}
}

function getAgentTokenLocal(scope = getCurrentChatScope()) {
  try {
    const safeScope = sanitizeScope(scope);

    const scopedKey = scopedStorageKey("agent_token", safeScope);
    const scoped = (localStorage.getItem(scopedKey) || sessionStorage.getItem(scopedKey) || "").trim();
    if (scoped) return scoped;

    const bridgeScope = readStorageValue("sessionStorage", "fm:agent_token:current_scope");
    const bridgeToken = readStorageValue("sessionStorage", "fm:agent_token:current");
    if (bridgeToken && bridgeScope && bridgeScope === safeScope) return bridgeToken;

    const scopedAccounts = safeParse(localStorage.getItem(scopedStorageKey("agent_accounts_v3", safeScope)) || "[]", []);
    const scopedIdx = Number(localStorage.getItem(scopedStorageKey("agent_active_idx", safeScope)) || 0);
    const scopedAccountToken = scopedAccounts?.[scopedIdx]?.token || "";
    if (scopedAccountToken) return String(scopedAccountToken).trim();

    const legacyOwner = localStorage.getItem("agent_token_owner_scope") || "";
    const legacy = (localStorage.getItem("agent_token") || "").trim();
    if (legacy && legacyOwner && legacyOwner === safeScope) return legacy;

    const legacyAccountsOwner = localStorage.getItem("agent_accounts_owner_scope") || "";
    if (legacyAccountsOwner && legacyAccountsOwner === safeScope) {
      const list = safeParse(localStorage.getItem("agent_accounts_v3") || "[]", []);
      const idx = Number(localStorage.getItem("agent_active_idx") || 0);
      const tok = list?.[idx]?.token || "";
      if (tok) return String(tok).trim();
    }

    return null;
  } catch {
    return null;
  }
}

async function fetchServerAgentTokenForChat(scope = getCurrentChatScope()) {
  try {
    const data = await fetchJsonForChat("/api/agent/tokens", { method: "GET" });
    if (data?.error) return null;

    const items = Array.isArray(data?.tokens) ? data.tokens : [];
    if (!items.length) return null;

    const sorted = [...items].sort((a, b) => {
      const aActive = a?.active !== false ? 1 : 0;
      const bActive = b?.active !== false ? 1 : 0;
      if (aActive !== bActive) return bActive - aActive;

      const ad = Date.parse(a?.last_used_at || a?.created_at || 0) || 0;
      const bd = Date.parse(b?.last_used_at || b?.created_at || 0) || 0;
      return bd - ad;
    });

    const selected = sorted.find((item) => item?.token && item?.active !== false) || sorted.find((item) => item?.token);
    const token = String(selected?.token || "").trim();
    if (!token) return null;

    rememberAgentTokenForChat(token, scope);
    return token;
  } catch {
    return null;
  }
}

async function getAgentTokenForTrading(scope = getCurrentChatScope()) {
  const activeScope = setActiveChatScope(scope || resolveStoredChatScope());
  const localToken = getAgentTokenLocal(activeScope);
  if (localToken) return localToken;
  return await fetchServerAgentTokenForChat(activeScope);
}

/* ---------- Composant ---------- */
export default function ChatPage() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [canSend, setCanSend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [typingText, setTypingText] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [longPressId, setLongPressId] = useState(null);
  const [showDownBtn, setShowDownBtn] = useState(false);
  const [isMultiline, setIsMultiline] = useState(false);
  const [viewerName, setViewerName] = useState(() => "");
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [compactComposerPlaceholder, setCompactComposerPlaceholder] = useState(() => {
    if (typeof window === "undefined") return false;
    const vw = Math.min(
      window.innerWidth || 0,
      document.documentElement?.clientWidth || window.innerWidth || 0
    );
    return vw > 0 && vw <= 380;
  });

  const pressTimer = useRef(null);
  const welcomeTimersRef = useRef([]);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const inputValueRef = useRef("");
  const canSendRef = useRef(false);
  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  const containerRef = useRef(null);
  const atBottomRef = useRef(true);
  const forceAutoScrollRef = useRef(false);
  const orderDraftRef = useRef({ symbol: "", lot_size: undefined, side: "" });
  const sendInFlightRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Démarrage propre : pas de prénom/token legacy global réutilisé par erreur.
    clearViewerNameCache();
    setViewerName("");
    setActiveChatScope("anonymous");
  }, []);

  useEffect(() => {
    const onAuthStorageChange = (event) => {
      if (!AUTH_STORAGE_KEYS_FOR_CHAT.includes(event.key)) return;

      const scopeToClear = getCurrentChatScope();
      clearViewerNameCache(scopeToClear);

      if (event.oldValue && event.oldValue !== event.newValue) {
        clearChatLocalForScope(scopeToClear);
        orderDraftRef.current = { symbol: "", lot_size: undefined, side: "" };
        setViewerName("");
        setTypingText("");
        setMessages([]);
        setActiveChatScope("anonymous");
      }
    };

    window.addEventListener("storage", onAuthStorageChange);
    return () => window.removeEventListener("storage", onAuthStorageChange);
  }, []);

  /* Thème */
  useEffect(() => {
    const onCustom = (e) => {
      const mode = e?.detail;
      if (mode === "dark" || mode === "light") {
        try { localStorage.setItem("fm_theme", mode); } catch { }
        fmSetDark(mode === "dark");
      }
    };
    const onStorage = (ev) => {
      if (ev.key === "fm_theme") {
        const v = ev.newValue;
        if (v === "dark" || v === "light") fmSetDark(v === "dark");
      }
    };
    window.addEventListener("fm:set-theme", onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("fm:set-theme", onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  /* Composer calé */
  useLayoutEffect(() => {
    const el = containerRef.current;
    const setVar = (l, r) => {
      document.documentElement.style.setProperty("--fm-c-left", `${Math.max(0, Math.round(l))}px`);
      document.documentElement.style.setProperty("--fm-c-right", `${Math.max(0, Math.round(r))}px`);
    };
    const compute = () => {
      const node = containerRef.current; if (!node) return setVar(0, 0);
      const rect = node.getBoundingClientRect();
      // clientWidth EXCLUT la scrollbar verticale → pas de débordement à droite.
      const vw = document.documentElement.clientWidth || window.innerWidth || 0;
      setVar(rect.left, vw - rect.right);
    };
    compute();
    const onResize = () => compute();
    window.addEventListener("resize", onResize, { passive: true });
    const ro = el ? new ResizeObserver(compute) : null;
    ro?.observe(el);
    el?.parentElement && ro?.observe(el.parentElement);
    const mo = new MutationObserver(compute);
    mo.observe(document.body, { attributes: true, attributeFilter: ["class", "style"], subtree: true });
    let raf = 0;
    const animate = () => { compute(); raf = requestAnimationFrame(animate); };
    const kick = () => { cancelAnimationFrame(raf); animate(); setTimeout(() => cancelAnimationFrame(raf), 350); };
    document.addEventListener("transitionstart", kick, true);
    document.addEventListener("animationstart", kick, true);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("transitionstart", kick, true);
      document.removeEventListener("animationstart", kick, true);
      ro?.disconnect();
      mo.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  /* VisualViewport offset mobile
     Corrige le vide blanc sous la barre d'entrée :
     - on ignore les petits offsets produits par la barre du navigateur mobile ;
     - on ne remonte le composer que lorsqu'un vrai clavier est ouvert ;
     - on garde le fond global aligné avec le thème pour éviter l'apparition du body blanc. */
  useEffect(() => {
    let rafId = 0;

    const setViewportVars = () => {
      cancelAnimationFrame(rafId);

      rafId = requestAnimationFrame(() => {
        const vv = window.visualViewport;
        const doc = document.documentElement;
        const layoutHeight = Math.max(
          window.innerHeight || 0,
          doc.clientHeight || 0,
          vv?.height || 0
        );
        const viewportHeight = vv?.height || layoutHeight || 0;
        const viewportOffsetTop = vv?.offsetTop || 0;
        const viewportWidth = Math.min(
          window.innerWidth || doc.clientWidth || 0,
          doc.clientWidth || window.innerWidth || 0
        );

        const rawOffset = vv
          ? Math.max(0, layoutHeight - viewportHeight - viewportOffsetTop)
          : 0;

        const textareaFocused = !!textareaRef.current && document.activeElement === textareaRef.current;
        const isMobile = viewportWidth < 640;

        // Les offsets de 20/60/90px viennent souvent des barres Chrome/Safari,
        // pas du clavier. Les appliquer crée le petit bloc blanc sous l'input.
        const keyboardOpen = isMobile && textareaFocused && rawOffset > 120;
        const safeOffset = keyboardOpen ? rawOffset : 0;

        doc.style.setProperty("--fm-vv-offset", `${Math.round(safeOffset)}px`);
        doc.style.setProperty("--fm-vv-h", `${Math.round(viewportHeight)}px`);
        doc.classList.toggle("fm-keyboard-open", keyboardOpen);

        if (keyboardOpen && atBottomRef.current && scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    };

    const vv = window.visualViewport;
    setViewportVars();

    vv?.addEventListener("resize", setViewportVars);
    vv?.addEventListener("scroll", setViewportVars);
    window.addEventListener("resize", setViewportVars, { passive: true });
    window.addEventListener("orientationchange", setViewportVars);
    document.addEventListener("focusin", setViewportVars);
    document.addEventListener("focusout", setViewportVars);

    return () => {
      cancelAnimationFrame(rafId);
      vv?.removeEventListener("resize", setViewportVars);
      vv?.removeEventListener("scroll", setViewportVars);
      window.removeEventListener("resize", setViewportVars);
      window.removeEventListener("orientationchange", setViewportVars);
      document.removeEventListener("focusin", setViewportVars);
      document.removeEventListener("focusout", setViewportVars);
      document.documentElement.classList.remove("fm-keyboard-open");
      document.documentElement.style.setProperty("--fm-vv-offset", "0px");
    };
  }, []);

  /* Placeholder mobile compact : évite que “Écrivez votre message…” passe sur 2 lignes sur petits écrans */
  useEffect(() => {
    const syncCompactPlaceholder = () => {
      const vw = Math.min(
        window.innerWidth || 0,
        document.documentElement?.clientWidth || window.innerWidth || 0
      );
      setCompactComposerPlaceholder(vw > 0 && vw <= 380);
    };

    syncCompactPlaceholder();
    window.addEventListener("resize", syncCompactPlaceholder, { passive: true });
    window.addEventListener("orientationchange", syncCompactPlaceholder);

    const vv = window.visualViewport;
    vv?.addEventListener("resize", syncCompactPlaceholder);

    return () => {
      window.removeEventListener("resize", syncCompactPlaceholder);
      window.removeEventListener("orientationchange", syncCompactPlaceholder);
      vv?.removeEventListener("resize", syncCompactPlaceholder);
    };
  }, []);

  /* Scroll helpers */
  const ensureBottom = useCallback(() => {
    const sc = scrollRef.current; if (!sc) return;
    sc.scrollTop = sc.scrollHeight;
  }, []);
  const scrollToBottom = useCallback((behavior = "smooth") => {
    try {
      if (behavior === "instant") ensureBottom();
      else messagesEndRef.current?.scrollIntoView({ behavior });
    } catch { }
  }, [ensureBottom]);
  const checkAtBottom = useCallback(() => {
    const el = scrollRef.current; if (!el) return;
    const isBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 24;
    atBottomRef.current = isBottom; setShowDownBtn(!isBottom);
  }, []);
  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const onScroll = () => checkAtBottom();
    el.addEventListener("scroll", onScroll, { passive: true });
    checkAtBottom();
    return () => el.removeEventListener("scroll", onScroll);
  }, [checkAtBottom]);
  useEffect(() => {
    const isWelcomeSequence = Array.isArray(messages) && messages.length > 0 && messages.every((msg) => [
      WELCOME_GREETING_TYPE,
      WELCOME_CONNECTION_TYPE,
      WELCOME_ACTIONS_TYPE,
    ].includes(msg.type));

    if (atBottomRef.current) {
      if (isWelcomeSequence) ensureBottom();
      else messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } else {
      checkAtBottom();
    }
  }, [messages, loading, typingText, checkAtBottom, ensureBottom]);
  const onStartTypingScroll = useCallback(async () => {
    forceAutoScrollRef.current = true;
    ensureBottom();
    await Promise.resolve();
  }, [ensureBottom]);

  const handleComposerInput = useCallback((e) => {
    const value = e?.currentTarget?.value || "";
    inputValueRef.current = value;

    const ready = value.trim().length > 0;
    if (canSendRef.current !== ready) {
      canSendRef.current = ready;
      setCanSend(ready);
    }
  }, []);

  // Message préenregistré : à l'arrivée (chat vide/accueil), on pré-remplit la
  // barre avec « Comment ça marche ? » pour que l'utilisateur puisse l'envoyer,
  // le modifier ou le supprimer sans chercher quoi écrire. Une seule fois.
  const presetPrefilledRef = useRef(false);
  useEffect(() => {
    if (presetPrefilledRef.current) return;
    if (!sessionReady) return;
    const onlyWelcome =
      !messages ||
      messages.length === 0 ||
      messages.every((m) =>
        [WELCOME_GREETING_TYPE, WELCOME_CONNECTION_TYPE, WELCOME_ACTIONS_TYPE].includes(m.type)
      );
    if (!onlyWelcome) return;
    const el = textareaRef.current;
    if (!el || el.value.trim()) return;
    presetPrefilledRef.current = true;
    const txt = t("chat.presetMessage");
    el.value = txt;
    inputValueRef.current = txt;
    canSendRef.current = true;
    setCanSend(true);
    handleComposerInput({ currentTarget: el });
  }, [sessionReady, messages, handleComposerInput, t]);

  /* Helpers messages */
  const addMessage = useCallback((author, text, date = new Date().toISOString(), extra = {}) => {
    const id = Date.now() + Math.random();
    const msg = { author, text, date, id, ...extra };
    setMessages((prev) => dedupeMessages([...prev, msg]));
    return id;
  }, []);
  const patchMessage = useCallback((tempId, patch) => {
    setMessages(prev => prev.map(m => (m.id === tempId ? { ...m, ...patch } : m)));
  }, []);

  const handleUnauthorized = useCallback(() => {
    const scopeToClear = getCurrentChatScope();
    clearChatLocalForScope(scopeToClear);
    orderDraftRef.current = { symbol: "", lot_size: undefined, side: "" };
    setViewerName("");
    clearViewerNameCache(scopeToClear);
    setTypingText("");
    setLoading(false);
    sendInFlightRef.current = false;
    setMessages([]);
    try { apiLogout(); } catch { }
    setErrorMessage(t("chat.auth.ssoRequiredError"));
    addMessage("ai", t("chat.auth.ssoRequiredMessage", { aiName: AI_NAME }));
    setTimeout(() => navigate("/sso", { replace: true }), 700);
  }, [navigate, addMessage]);


  const fetchViewerName = useCallback(async () => {
    const previousScope = getCurrentChatScope();

    // On vide l'affichage avant toute recherche : aucune chance d'afficher l'ancien compte.
    setViewerName("");

    // On supprime seulement les clés legacy globales dangereuses. On ne supprime pas encore
    // le cache scopé courant tant que le serveur n'a pas confirmé le vrai utilisateur.
    try {
      window.localStorage.removeItem("fm_user_display_name");
      window.localStorage.removeItem("fm_user_display_name_owner_scope");
      window.sessionStorage.removeItem("fm:user:display_name:current");
      window.sessionStorage.removeItem("fm:user:display_name:current_scope");
    } catch {}

    const applyProfile = (data, authToken = "") => {
      if (!data || data.error) return "";

      const identity = getUserIdentityValue(data);
      const nextScope = identity ? makeUserScope(identity) : previousScope;
      if (authToken) rememberAuthTokenForScope(authToken, nextScope);

      if (nextScope && nextScope !== previousScope) {
        setActiveChatScope(nextScope);
        orderDraftRef.current = { symbol: "", lot_size: undefined, side: "" };
        setMessages([]);
      } else {
        setActiveChatScope(nextScope || previousScope || "anonymous");
      }

      const name = pickUserDisplayName(data);
      if (name) {
        const stored = rememberViewerNameForScope(name, nextScope);
        setViewerName(stored);
        return stored;
      }

      const scopedName = resolveStoredViewerName(nextScope);
      if (scopedName) {
        setViewerName(scopedName);
        return scopedName;
      }

      return "";
    };

    // 1) Même logique que le Sidebar : apiProfile + getToken.
    try {
      const token = typeof getToken === "function" ? getToken() : "";
      if (token && typeof apiProfile === "function") {
        const data = await apiProfile(token);
        const name = applyProfile(data, token);
        if (name) return name;
      }
    } catch {}

    // 2) API session cookie/Bearer.
    const endpoints = ["/api/me", "/api/auth/me", "/api/profile"];
    const tokenCandidates = getTokenCandidatesForScope();

    for (const endpoint of endpoints) {
      const requestVariants = [
        {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
          __authToken: "",
        },
        {
          method: "GET",
          credentials: "include",
          headers: authHeadersForChat({ Accept: "application/json" }),
          __authToken: getBearerTokenForChat(),
        },
        ...tokenCandidates.map((token) => ({
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`
          },
          __authToken: token,
        }))
      ];

      for (const options of requestVariants) {
        try {
          const { __authToken, ...fetchOptions } = options;
          const res = await fetch(endpoint, fetchOptions);
          if (!res.ok) continue;
          const data = await res.json().catch(() => ({}));
          const name = applyProfile(data, __authToken || "");
          if (name) return name;
        } catch {}
      }
    }

    // 3) Dernier fallback fiable : payload du token actuel.
    for (const token of tokenCandidates) {
      const payload = decodeJwtPayload(token);
      if (!payload) continue;
      const identity = getUserIdentityValue(payload);
      const nextScope = identity ? makeUserScope(identity) : getCurrentChatScope();
      if (nextScope) {
        setActiveChatScope(nextScope);
        rememberAuthTokenForScope(token, nextScope);
      }
      const name = pickUserDisplayName(payload);
      if (name) {
        const stored = rememberViewerNameForScope(name, nextScope);
        setViewerName(stored);
        return stored;
      }
    }

    setViewerName("");
    return "";
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const name = await fetchViewerName();
      if (!cancelled) {
        setViewerName(name || "");
        setSessionReady(true);
      }
    })();

    return () => { cancelled = true; };
  }, [fetchViewerName]);


const clearWelcomeTimers = useCallback(() => {
  if (!welcomeTimersRef.current.length) return;
  welcomeTimersRef.current.forEach((timerId) => clearTimeout(timerId));
  welcomeTimersRef.current = [];
}, []);

const pushWelcomeSequence = useCallback((name = "") => {
  clearWelcomeTimers();

  const greetingDate = new Date().toISOString();
  addMessage("ai", "", greetingDate, {
    type: WELCOME_GREETING_TYPE,
    meta: buildWelcomeGreetingMeta(name, t)
  });
  ensureBottom();

  const t1 = setTimeout(() => {
    addMessage("ai", "", new Date().toISOString(), {
      type: WELCOME_CONNECTION_TYPE,
      meta: buildWelcomeConnectionMeta(t)
    });
    ensureBottom();
  }, 120);

  const t2 = setTimeout(() => {
    addMessage("ai", "", new Date().toISOString(), {
      type: WELCOME_ACTIONS_TYPE,
      meta: buildWelcomeActionsMeta(t)
    });
    ensureBottom();
  }, 240);

  welcomeTimersRef.current = [t1, t2];
}, [addMessage, clearWelcomeTimers, ensureBottom]);

useEffect(() => () => {
  clearWelcomeTimers();
}, [clearWelcomeTimers]);

useEffect(() => {
  if (!viewerName) return;

  setMessages(prev => prev.map((msg) => (
    msg.type === WELCOME_GREETING_TYPE
      ? { ...msg, meta: buildWelcomeGreetingMeta(viewerName, t) }
      : msg
  )));
}, [viewerName]);

  /* Persist DB; retourne null/err si KO. Ne déconnecte jamais l'utilisateur depuis une persistance optionnelle. */
  const persistMessage = useCallback(async ({ author, text = "", type = "text", meta = {}, client_request_id = null }) => {
    try {
      const res = await fetch(`${API_ROOT}/messages`, {
        method: "POST",
        headers: authHeadersForChat({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify({ author, text, type, meta, client_request_id })
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401 || res.status === 403) {
        // Important : /api/messages sert aux traces complémentaires (CTA, commandes, reçus).
        // On ne doit pas déconnecter l'utilisateur si cette persistance échoue.
        return { error: "unauthorized", status: res.status };
      }

      if (!res.ok) {
        return { error: data?.error || data?.message || `HTTP ${res.status}`, status: res.status };
      }

      return data?.message || null;
    } catch (e) {
      return { error: e.message };
    }
  }, []);

  const persistUserActivity = useCallback(async ({ text, type = "user_command", meta = {}, client_request_id = null }) => {
    return persistMessage({
      author: "user",
      text,
      type,
      meta,
      client_request_id,
    });
  }, [persistMessage]);

  /* CTA navigation */
  const pushNavCTA = useCallback(async ({ title, target, buttonLabel = t("chat.cta.open"), reason = "", draft = {} }) => {
    const crid = genCRID();
    const meta = {
      cta: { title: title || t("chat.cta.open"), target: target || "/", buttonLabel },
      reason,
      draft: { ...draft },
      crid,
      client_request_id: crid
    };
    try {
      const scope = getCurrentChatScope();
      const intentPayload = { target, reason, draft: meta.draft, owner_scope: scope, created_at: new Date().toISOString() };
      if (target === "/agent") {
        localStorage.setItem(scopedStorageKey("agent_intent_v1", scope), JSON.stringify(intentPayload));
        localStorage.setItem("agent_intent_v1", JSON.stringify(intentPayload));
      } else {
        localStorage.setItem(scopedStorageKey("nav_intent_v1", scope), JSON.stringify(intentPayload));
        localStorage.setItem("nav_intent_v1", JSON.stringify(intentPayload));
      }
    } catch { }

    const date = new Date().toISOString();
    const text = reason || t("chat.cta.openRequestedPage");
    const tmpId = addMessage("ai", text, date, { type: "cta", cta: meta.cta, meta });
    ensureBottom();
    const saved = await persistMessage({ author: "ai", text, type: "cta", meta, client_request_id: crid });
    if (saved?.id) {
      patchMessage(tmpId, { persistedId: saved.id });
      mirrorMessageLocally({ id: tmpId, persistedId: saved.id, author: "ai", text, date, type: "cta", meta });
    }
    else { mirrorMessageLocally({ id: tmpId, author: "ai", text, date, type: "cta", meta }); }
  }, [addMessage, patchMessage, persistMessage, ensureBottom]);

  const pushAgentCTA = useCallback(async (reason, draft = {}) => {
    await pushNavCTA({
      title: t("chat.cta.agentTitle"),
      target: "/agent",
      buttonLabel: t("chat.cta.openAgent"),
      reason: reason || t("chat.cta.openAgentToFinalize"),
      draft
    });
  }, [pushNavCTA]);

  /* Charge Historique (serveur + local mirror) */
  const loadHistory = useCallback(async () => {
    // Ne pas recalculer le scope depuis de vieilles clés locales :
    // fetchViewerName() fixe le scope à partir de /api/me.
    if (!getCurrentChatScope()) setActiveChatScope("anonymous");
    setErrorMessage("");
    try {
      const res = await fetchHistoryForChat();
      if (res?.error) {
        if (res?.status === 401 || res?.status === 403 || /(unauthori|forbidden|Non autorisé)/i.test(res.error || "")) {
          // L'historique est optionnel ici. Ne jamais casser l'accueil ni vider le prénom
          // si /api/history refuse la session alors que /api/me a déjà identifié l'utilisateur.
          // On affiche simplement l'historique local du compte courant, sinon l'accueil.
          setErrorMessage("");
          const localsOnly = dedupeMessages(readLocal(getCurrentChatScope()));
          setMessages(localsOnly);
          if (!localsOnly.length) {
            pushWelcomeSequence(viewerName || resolveStoredViewerName(getCurrentChatScope()));
          }
          return;
        }
        setErrorMessage(res.error || t("chat.history.fetchError"));
        const localsOnly = dedupeMessages(readLocal());
        setMessages(localsOnly);
        if (!localsOnly.length) pushWelcomeSequence(viewerName);
      } else {
        const raw = Array.isArray(res) ? res : Array.isArray(res?.messages) ? res.messages : [];
        const serverList = raw.map(m => {
          const base = {
            id: m.id || Date.now() + Math.random(),
            persistedId: m.id,
            author: m.author || "ai",
            text: m.text || "",
            date: m.timestamp || m.created_at || new Date().toISOString(),
            type: m.type || "text",
            meta: m.meta || {}
          };
          if (base.type === "cta" && !base.cta) base.cta = base.meta?.cta;
          return base;
        });

        const locals = readLocal();
        const serverKeys = new Set(serverList.map(messageDedupeKey));
        const serverFPs = new Set(serverList.map(fp));
        const localToAdd = (Array.isArray(locals) ? locals : []).filter(x => {
          // Type-aware : un reçu Agent et le message utilisateur peuvent partager le même CRID.
          // On retire seulement le local qui existe déjà côté serveur avec le même author + type + CRID.
          if (serverKeys.has(messageDedupeKey(x))) return false;
          if (x._fp && serverFPs.has(x._fp)) return false;
          return LOCAL_MIRRORED_TYPES.has(x.type || "text") && (x.author === "user" || x.author === "ai");
        }).map(x => ({
          id: Date.now() + Math.random(),
          persistedId: x.persistedId || null,
          author: x.author,
          text: x.text || "",
          date: x.date || new Date().toISOString(),
          type: x.type || "text",
          meta: x.meta || {},
          cta: x?.meta?.cta
        }));

        const list = dedupeMessages([...serverList, ...localToAdd]);
        setMessages(list);

        if (!list.length) {
          pushWelcomeSequence(viewerName || resolveStoredViewerName(getCurrentChatScope()));
        }
      }
    } catch (e) {
      setErrorMessage(t("chat.history.systemLoadError", { message: e.message }));
      addMessage("ai", t("chat.history.loadErrorGreeting", { aiName: AI_NAME }));
    }
  }, [addMessage, handleUnauthorized, pushWelcomeSequence, viewerName]);
  useEffect(() => {
    if (!sessionReady) return;
    loadHistory();
  }, [sessionReady, loadHistory]);

  /* Textarea auto-height */
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    const isMobileViewport = () => {
      const vw = Math.min(window.innerWidth || 0, document.documentElement.clientWidth || window.innerWidth || 0);
      return vw < 640;
    };

    const getViewportHeight = () =>
      window.visualViewport?.height ||
      window.innerHeight ||
      document.documentElement.clientHeight ||
      720;

    const getMaxH = () => {
      const vh = getViewportHeight();
      if (isMobileViewport()) return Math.min(178, Math.max(84, Math.round(vh * 0.24)));
      return Math.min(320, Math.max(118, Math.round(vh * 0.34)));
    };

    const oneLine = (() => {
      const cs = getComputedStyle(el);
      const lh = parseFloat(cs.lineHeight) || 22;
      const py = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      return Math.ceil(lh + py);
    })();

    const resize = () => {
      const maxH = getMaxH();

      el.style.maxHeight = `${maxH}px`;
      el.style.height = "auto";

      const next = Math.min(el.scrollHeight, maxH);
      el.style.height = `${next}px`;
      el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden";

      const multilineNext = next > (oneLine + 4);
      setIsMultiline((prev) => (prev === multilineNext ? prev : multilineNext));

      const composerMax = isMobileViewport() ? 248 : 420;
      const ch = Math.max(58, Math.min(composerMax, Math.round(composerRef.current?.offsetHeight || next || 88)));
      document.documentElement.style.setProperty("--fm-composer-h", `${ch}px`);
    };

    resize();

    el.addEventListener("input", resize);
    window.addEventListener("resize", resize, { passive: true });

    const vv = window.visualViewport;
    vv?.addEventListener("resize", resize);
    vv?.addEventListener("scroll", resize);

    return () => {
      el.removeEventListener("input", resize);
      window.removeEventListener("resize", resize);
      vv?.removeEventListener("resize", resize);
      vv?.removeEventListener("scroll", resize);
    };
  }, []);

  useEffect(() => () => { if (pressTimer.current) clearTimeout(pressTimer.current); }, []);

  const brandifyReply = (text) => {
    if (typeof text !== "string") return text;
    let out = text;
    out = out.replace(/\bchat\s*gpt\b/gi, AI_NAME);
    out = out.replace(/\bchatgpt\b/gi, AI_NAME);
    out = out.replace(/\bgpt(?:-\d+)?\b/gi, AI_NAME);
    out = out.replace(/\bopen[-\s]?ai\b/gi, AI_NAME);
    out = out.replace(/assistant(?:e)?\s+IA/gi, `assistant IA ${AI_NAME}`);
    return out;
  };

  // Simulation typing courte : l'animation reste premium, mais la réponse complète apparaît vite.
  const simulateTyping = useCallback(async (fullText, author, date, { noBrand = false, leadInMs = 55, skipPersist = false } = {}) => {
    await onStartTypingScroll();
    const final = noBrand ? String(fullText || "") : brandifyReply(String(fullText || ""));
    const previewLimit = final.length > 360 ? 34 : Math.min(final.length, 54);
    setTypingText("");
    await sleep(Math.max(0, leadInMs));

    if (previewLimit <= 0) {
      const tmpId = addMessage(author, final, date);
      setLoading(false);
      forceAutoScrollRef.current = false;
      if (!skipPersist) {
        persistMessage({ author, text: final, type: "text" }).then(saved => {
          if (saved?.id) patchMessage(tmpId, { persistedId: saved.id });
        });
      }
      return tmpId;
    }

    let i = 0;
    await new Promise((resolve) => {
      const id = setInterval(() => {
        i += 2;
        setTypingText(final.substring(0, Math.min(i, previewLimit)));
        if (forceAutoScrollRef.current || atBottomRef.current) ensureBottom();
        if (i >= previewLimit) {
          clearInterval(id);
          resolve();
        }
      }, 4);
    });

    const tmpId = addMessage(author, final, date);
    setTypingText("");
    setLoading(false);
    forceAutoScrollRef.current = false;
    if (!skipPersist) {
      persistMessage({ author, text: final, type: "text" }).then(saved => {
        if (saved?.id) patchMessage(tmpId, { persistedId: saved.id });
      });
    }
    return tmpId;
  }, [addMessage, persistMessage, patchMessage, onStartTypingScroll, ensureBottom]);

  /* ---------- Chat IA via /api/chat (pas de /api/chat/stream pour éviter les 405 et les doublons) ---------- */
  const streamChat = useCallback(async (userText, userTempId = null) => {
    const date = new Date().toISOString();
    await onStartTypingScroll();
    setTypingText("");
    setLoading(true);

    const attachUserPersistedId = (payload) => {
      const persistedId = payload?.user_message?.id || payload?.userMessage?.id || payload?.user_message_id || null;
      if (persistedId && userTempId) {
        patchMessage(userTempId, { persistedId });
      }
    };

    const attachAiPersistedId = (payload, aiTempId) => {
      const persistedId = payload?.ai_message?.id || payload?.aiMessage?.id || payload?.ai_message_id || null;
      if (persistedId && aiTempId) {
        patchMessage(aiTempId, { persistedId });
      }
    };

    try {
      const ai = await sendChatForChat(userText);

      if (ai?.error) {
        if (ai?.status === 401 || ai?.status === 403 || /(Non autorisé|expiré|unauthori|forbidden)/i.test(ai.error)) {
          // Dernière synchronisation de session : le token validé par /api/me/apiProfile
          // est mémorisé par fetchViewerName(), puis /api/chat est retenté une seule fois.
          const refreshedName = await fetchViewerName();
          if (refreshedName) {
            const retryAi = await sendChatForChat(userText);
            if (!retryAi?.error) {
              attachUserPersistedId(retryAi);
              const retryRaw = typeof retryAi?.reply === "string" ? retryAi.reply : typeof retryAi === "string" ? retryAi : t("chat.stream.noReply");
              const retryTempId = await simulateTyping(retryRaw, "ai", date, { skipPersist: true });
              attachAiPersistedId(retryAi, retryTempId);
              return;
            }
          }

          setErrorMessage(t("chat.stream.sessionUnrecognized"));
          setLoading(false);
          forceAutoScrollRef.current = false;
          return;
        }

        setErrorMessage(ai.error || t("chat.stream.aiError"));
        await simulateTyping(t("chat.stream.errorOccurred", { error: ai.error }), "ai", date, { skipPersist: true });
        return;
      }

      attachUserPersistedId(ai);

      const raw = typeof ai?.reply === "string" ? ai.reply : typeof ai === "string" ? ai : t("chat.stream.noReply");
      const parsed = tryParseJSONBlocks(raw);

      if (parsed && looksLikeAgentPlan(parsed)) {
        setTypingText("");
        setLoading(false);
        forceAutoScrollRef.current = false;
        await pushAgentCTA(t("chat.stream.jsonPlanDetected"), parsed);
        return;
      }

      // /api/chat persiste déjà le message utilisateur + la réponse IA côté backend.
      // Les IDs renvoyés permettent une suppression définitive immédiatement,
      // sans attendre un rechargement de l'historique.
      const aiTempId = await simulateTyping(raw, "ai", date, { skipPersist: true });
      attachAiPersistedId(ai, aiTempId);
    } catch (err) {
      setErrorMessage(t("chat.stream.connectionError", { message: err.message }));
      await simulateTyping(t("chat.stream.connectionErrorTyping"), "ai", date, { skipPersist: true });
    } finally {
      forceAutoScrollRef.current = false;
      setLoading(false);
    }
  }, [simulateTyping, pushAgentCTA, onStartTypingScroll, fetchViewerName, patchMessage]);


  /* ---------- ENVOI ---------- */
  const handleSend = async (e) => {
    e?.preventDefault();
    const userText = (textareaRef.current?.value || inputValueRef.current || "").trim();
    if (!userText || loading || sendInFlightRef.current) return;
    sendInFlightRef.current = true;
    setErrorMessage(""); setSuccessMessage("");
    ensureBottom();
    const now = new Date().toISOString();

    // Brouillon multi-tours (capture symbol/side/lot)
    orderDraftRef.current = mergeDraft(orderDraftRef.current, extractPartialFromText(userText));

    // Message utilisateur affiché immédiatement.
    // Pour le chat général, /api/chat persiste déjà user + réponse.
    // Pour les commandes/navigation, on persiste plus bas avec un type dédié.
    const tmpUserId = addMessage("user", userText, now);

    inputValueRef.current = "";
    canSendRef.current = false;

    if (textareaRef.current) {
      textareaRef.current.value = "";
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.overflowY = "hidden";
    }

    setInput("");
    setCanSend(false);
    setIsMultiline(false);
    document.documentElement.style.setProperty("--fm-composer-h", `${Math.round(composerRef.current?.offsetHeight || 58)}px`);
    setLoading(true);

    const completeSend = () => {
      setLoading(false);
      setTypingText("");
      forceAutoScrollRef.current = false;
      sendInFlightRef.current = false;
    };

    // Message préenregistré « Comment ça marche ? » → onboarding scripté LOCAL
    // (aucun appel backend). Explication FR/EN + bouton « Installer l'Agent IA ».
    if (HOWTO_PRESETS.has(howtoKeyOf(userText))) {
      addMessage("ai", t("chat.onboarding.body"));
      await pushNavCTA({
        title: t("chat.onboarding.ctaTitle"),
        target: "/agent",
        buttonLabel: t("chat.quickActions.agent.label"),
        reason: t("chat.onboarding.ctaReason"),
      });
      completeSend();
      return;
    }

    // Intent navigation (agent/backtest/stratégies)
    const nav = detectNavIntent(userText, t);
    if (nav) {
      const navCrid = genCRID();
      persistUserActivity({
        text: userText,
        type: "user_navigation",
        client_request_id: navCrid,
        meta: { target: nav.target, title: nav.title, source: "chat", client_request_id: navCrid },
      }).then(saved => {
        if (saved?.id) {
          patchMessage(tmpUserId, { persistedId: saved.id, type: "user_navigation" });
          mirrorMessageLocally({
            id: tmpUserId,
            persistedId: saved.id,
            author: "user",
            text: userText,
            date: now,
            type: "user_navigation",
            meta: { target: nav.target, title: nav.title, source: "chat", client_request_id: navCrid },
          });
        } else {
          persistLocalFallbackMessage({
            id: tmpUserId,
            author: "user",
            text: userText,
            date: now,
            type: "user_navigation",
            meta: { target: nav.target, title: nav.title, source: "chat", client_request_id: navCrid },
          });
        }
      });

      await pushNavCTA({
        title: nav.title,
        target: nav.target,
        buttonLabel: t("chat.cta.openNamed", { name: nav.title }),
        reason: nav.reason
      });
      completeSend();
      return;
    }

    // 1) Détection trading (hard text + parser)
    let parsed = null;
    let tradeIntent = isTradingText(userText);
    try {
      parsed = await apiParseMessage(userText);
      tradeIntent = tradeIntent || isTradingIntent(parsed, userText);
    } catch { }

    // 2) Si trading → Agent obligatoire
    if (tradeIntent) {
      const commandCrid = genCRID();
      const commandMeta = {
        source: "chat",
        parsed: parsed || {},
        draft: { ...orderDraftRef.current },
        client_request_id: commandCrid,
      };

      // Sécurité UX : une commande ne doit jamais disparaître si /api/messages répond 401/405
      // ou si l'utilisateur quitte la page avant la réponse serveur.
      persistLocalFallbackMessage({
        id: tmpUserId,
        author: "user",
        text: userText,
        date: now,
        type: "user_command",
        meta: commandMeta,
      });

      persistUserActivity({
        text: userText,
        type: "user_command",
        client_request_id: commandCrid,
        meta: commandMeta,
      }).then(saved => {
        if (saved?.id) {
          patchMessage(tmpUserId, { persistedId: saved.id, type: "user_command", meta: commandMeta });
          mirrorMessageLocally({
            id: tmpUserId,
            persistedId: saved.id,
            author: "user",
            text: userText,
            date: now,
            type: "user_command",
            meta: commandMeta,
          });
        } else {
          persistLocalFallbackMessage({
            id: tmpUserId,
            author: "user",
            text: userText,
            date: now,
            type: "user_command",
            meta: commandMeta,
          });
        }
      });

      const agentToken = await getAgentTokenForTrading();
      if (!agentToken) {
        persistLocalFallbackMessage({
          id: tmpUserId,
          author: "user",
          text: userText,
          date: now,
          type: "user_command",
          meta: { ...commandMeta, status: "waiting_agent_token" },
        });
        await pushAgentCTA(t("chat.trade.noAgentToken"));
        completeSend();
        return;
      }

      const crid = commandCrid;

      // Respect du lot si l'utilisateur le précise (sinon on garde le draft multi-tours)
      const lotInMessage = guessLotFrom(userText);
      const sideInMessage = guessSideFrom(userText);

      const preferredLot =
        (typeof lotInMessage === "number" && Number.isFinite(lotInMessage))
          ? lotInMessage
          : (typeof orderDraftRef.current?.lot_size === "number" && Number.isFinite(orderDraftRef.current.lot_size))
            ? orderDraftRef.current.lot_size
            : undefined;

      const preferredSide =
        (sideInMessage || orderDraftRef.current?.side || "").toLowerCase() || undefined;

      const norm = ` ${normalize(userText)} `;
      const isCloseAll =
        /\bclose\s*all\b/.test(norm) ||
        (/\bferme\w*\b/.test(norm) && /\b(tout|toute|toutes)\b/.test(norm) && /\b(position|positions)\b/.test(norm)) ||
        (/\bclotur\w*\b/.test(norm) && /\b(tout|toute|toutes)\b/.test(norm) && /\b(position|positions)\b/.test(norm));

      // Fast path: fermeture TOUTES positions (close_all) -> envoi direct Agent (sans symbol/lot)
      if (isCloseAll) {
        const payloadToSend = { cmd: "close_all", client_request_id: crid, meta: { source: "chat", locale: "fr-FR" } };
        const r = await apiAgentCommand(agentToken, payloadToSend);
        if (r?.error) {
          await pushAgentCTA(t("chat.trade.commandRefused", { error: r.error }), payloadToSend);
          completeSend();
          return;
        }

        const text = t("chat.trade.orderSent");
        const meta = { payload: payloadToSend, tickets: r?.tickets || [], status: "ok", client_request_id: crid, crid };
        const tmpId = addMessage("ai", text, now, { type: "order_receipt", meta });

        const saved = await persistMessage({ author: "ai", text, type: "order_receipt", meta, client_request_id: crid });
        if (saved?.id) {
          patchMessage(tmpId, { persistedId: saved.id });
          mirrorMessageLocally({ id: tmpId, persistedId: saved.id, author: "ai", text, date: now, type: "order_receipt", meta });
        } else {
          mirrorMessageLocally({ id: tmpId, author: "ai", text, date: now, type: "order_receipt", meta });
        }

        orderDraftRef.current = { symbol: "", lot_size: undefined, side: "" };
        completeSend();
        return;
      }

      const hintSymbol = keepSymbol(orderDraftRef.current?.symbol || guessSymbolFromFreeText(userText) || "");


// Fast path: ouverture "market" simple (BUY/SELL + symbole [+ lot]) -> envoi direct à l’Agent
// Objectif: éviter les ambiguïtés du wizard et garantir que le lot/symbole envoyés sont EXACTEMENT ceux demandés.
const fastCmd = synthesizeSimpleOpenMarket(userText, orderDraftRef.current);
if (fastCmd) {
  const payloadToSend = {
    ...fastCmd,
    client_request_id: crid,
    meta: { source: "chat", locale: "fr-FR" }
  };

  // Si l'utilisateur a explicitement donné un lot dans le message, on force partout
  if (typeof lotInMessage === "number" && Number.isFinite(lotInMessage)) {
    payloadToSend.volume = lotInMessage;
    payloadToSend.lot_size = lotInMessage;
    payloadToSend.lot = lotInMessage;
  }

  const r = await apiAgentCommand(agentToken, payloadToSend);
  if (r?.error) {
    await pushAgentCTA(t("chat.trade.commandRefused", { error: r.error }), payloadToSend);
    completeSend();
    return;
  }

  const text = t("chat.trade.orderSent");
  const meta = { payload: payloadToSend, tickets: r?.tickets || [], status: "ok", client_request_id: crid, crid };
  const tmpId = addMessage("ai", text, now, { type: "order_receipt", meta });

  const saved = await persistMessage({ author: "ai", text, type: "order_receipt", meta, client_request_id: crid });
  if (saved?.id) {
    patchMessage(tmpId, { persistedId: saved.id });
    mirrorMessageLocally({ id: tmpId, persistedId: saved.id, author: "ai", text, date: now, type: "order_receipt", meta });
  } else {
    mirrorMessageLocally({ id: tmpId, author: "ai", text, date: now, type: "order_receipt", meta });
  }

  orderDraftRef.current = { symbol: "", lot_size: undefined, side: "" };
  completeSend();
  return;
}

      try {
        // Wizard prioritaire (meilleur pour "tous marchés" car l’Agent peut mapper)
        try {
          if (typeof apiAgentWizardExecute === "function") {
            const wz = await apiAgentWizardExecute(agentToken, {
              nl: userText,
              autoconfirm: true,
              allow_defaults: true,
              client_request_id: crid,
// Hints/overrides en top-level (meilleure compatibilité backend/agent)
symbol_hint: hintSymbol || undefined,
side_hint: preferredSide || undefined,
volume: (typeof preferredLot === "number" && Number.isFinite(preferredLot)) ? preferredLot : undefined,
lot_size: (typeof preferredLot === "number" && Number.isFinite(preferredLot)) ? preferredLot : undefined,
lot: (typeof preferredLot === "number" && Number.isFinite(preferredLot)) ? preferredLot : undefined,
              meta: {
                source: "chat",
                locale: "fr-FR",
                symbol_hint: hintSymbol || undefined,
                side_hint: preferredSide || undefined,
                volume_hint: (typeof preferredLot === "number" && Number.isFinite(preferredLot)) ? preferredLot : undefined,
              },
});

            if (wz && !wz.error) {
              if (wz.partial) {
                orderDraftRef.current = mergeDraft(orderDraftRef.current, {
                  symbol: keepSymbol(wz.partial.symbol || hintSymbol || ""),
                  lot_size: wz.partial.lot_size ?? wz.partial.volume,
                  side: wz.partial.side
                });
              }

              if (wz.status === "ask" || wz.ask || wz.question || wz.next_question) {
                await pushAgentCTA(t("chat.trade.ambiguousCommand"), wz.partial || {});
                completeSend();
                return;
              }

              if (wz.status === "executed" || wz.status === "queued") {
                const text = t("chat.trade.orderSent");
                const payload = wz.order || wz.payload || {};
                if (payload?.symbol) payload.symbol = keepSymbol(payload.symbol);

                // Respect du lot si l'utilisateur l'a explicitement précisé
                const payloadVol = payload?.volume ?? payload?.lot_size ?? payload?.lot;
                if (typeof lotInMessage === "number" && Number.isFinite(lotInMessage)) {
                  payload.volume = lotInMessage;
                  payload.lot_size = lotInMessage;
                  payload.lot = lotInMessage;
} else if (!(typeof payloadVol === "number" && Number.isFinite(payloadVol)) && typeof preferredLot === "number" && Number.isFinite(preferredLot)) {
                  payload.volume = preferredLot;
                  payload.lot_size = preferredLot;
                  payload.lot = preferredLot;
                }

                // Side hint (si l'Agent ne l'a pas inclus)
                if (!payload?.side && preferredSide) payload.side = preferredSide;

                const meta = { payload, tickets: wz.tickets || [], status: "ok", client_request_id: crid, crid };
                const tmpId = addMessage("ai", text, now, { type: "order_receipt", meta });
                const saved = await persistMessage({ author: "ai", text, type: "order_receipt", meta, client_request_id: crid });
                if (saved?.id) {
                  patchMessage(tmpId, { persistedId: saved.id });
                  mirrorMessageLocally({ id: tmpId, persistedId: saved.id, author: "ai", text, date: now, type: "order_receipt", meta });
                } else {
                  mirrorMessageLocally({ id: tmpId, author: "ai", text, date: now, type: "order_receipt", meta });
                }
                orderDraftRef.current = { symbol: "", lot_size: undefined, side: "" };
                completeSend();
                return;
              }
            }
          }
        } catch {
          // silence -> fallback below
        }

        // Fallback: build + synthèse locale
        let baseCmd = buildEACommand(parsed) || null;

        // Close all hard
        if (!baseCmd) {
          const s = ` ${normalize(userText)} `;
          if (/\bclose\s*all\b/.test(s) || /\bfermer?\b.*\b(tout|toutes?|positions?)\b/.test(s) || /\bclotur\w*\b.*\b(tout|toutes?|positions?)\b/.test(s)) {
            baseCmd = { cmd: "close_all" };
          }
        }

        // Synthèse locale (market) si côté + symbole connus
        if (!baseCmd) {
          const synthesized = trySynthesizeCmd(userText, orderDraftRef.current);
          if (synthesized) baseCmd = synthesized;
        }

        if (!baseCmd) {
          await pushAgentCTA(t("chat.trade.tradingDetected"), orderDraftRef.current);
          completeSend();
          return;
        }

        // Normalise symbol partout (Deriv safe) — close_all n'a pas besoin de symbole
        const baseCmdTypeLower = String(baseCmd.cmd || baseCmd.command || baseCmd.kind || "").toLowerCase();
        if (baseCmdTypeLower !== "close_all") {
          if (baseCmd.symbol) baseCmd.symbol = keepSymbol(baseCmd.symbol);
          if (!baseCmd.symbol && hintSymbol) baseCmd.symbol = hintSymbol;
        } else {
          if (baseCmd.symbol) delete baseCmd.symbol;
        }

        // Respect du lot (si explicitement dans le message → priorité, sinon draft multi-tours)
        const lotPreferredToSend =
          (typeof lotInMessage === "number" && Number.isFinite(lotInMessage))
            ? lotInMessage
            : (typeof orderDraftRef.current?.lot_size === "number" && Number.isFinite(orderDraftRef.current.lot_size))
              ? orderDraftRef.current.lot_size
              : undefined;

        const cmdType = String(baseCmd.cmd || baseCmd.command || baseCmd.kind || "").toLowerCase();
        const isOpenOrder =
          ["market", "pending", "buy", "sell", "buy_limit", "buy_stop", "sell_limit", "sell_stop"].includes(cmdType) ||
          (!!baseCmd.side && !!baseCmd.symbol && !["modify", "close", "close_all", "breakeven", "trailing"].includes(cmdType));

        if (isOpenOrder && !baseCmd.side && preferredSide) baseCmd.side = preferredSide;

        if (isOpenOrder && typeof lotPreferredToSend === "number" && Number.isFinite(lotPreferredToSend)) {
          baseCmd.volume = lotPreferredToSend;
          baseCmd.lot_size = lotPreferredToSend;
          baseCmd.lot = lotPreferredToSend;
}

        if (requiresSymbol(baseCmd)) {
          await pushAgentCTA(t("chat.trade.symbolRequired"), {
            ...baseCmd,
            symbol: baseCmd.symbol || hintSymbol || ""
          });
          completeSend();
          return;
        }

        // Ajoute CRID + meta au payload (si l’API l’accepte, sinon ignoré côté backend)
        const payloadToSend = {
          ...baseCmd,
          client_request_id: crid,
          meta: { source: "chat", locale: "fr-FR" }
        };

        const r = await apiAgentCommand(agentToken, payloadToSend);
        if (r?.error) {
          await pushAgentCTA(t("chat.trade.commandRefused", { error: r.error }), payloadToSend);
          completeSend();
          return;
        }

        const text = t("chat.trade.orderSent");
        const meta = { payload: payloadToSend, tickets: r?.tickets || [], status: "ok", client_request_id: crid, crid };
        const tmpId = addMessage("ai", text, now, { type: "order_receipt", meta });

        const saved = await persistMessage({ author: "ai", text, type: "order_receipt", meta, client_request_id: crid });
        if (saved?.id) {
          patchMessage(tmpId, { persistedId: saved.id });
          mirrorMessageLocally({ id: tmpId, persistedId: saved.id, author: "ai", text, date: now, type: "order_receipt", meta });
        } else {
          mirrorMessageLocally({ id: tmpId, author: "ai", text, date: now, type: "order_receipt", meta });
        }

        orderDraftRef.current = { symbol: "", lot_size: undefined, side: "" };
        completeSend();
        return;

      } catch (err) {
        await pushAgentCTA(t("chat.trade.networkError", { message: err.message }));
        completeSend();
        return;
      }
    }

    // 3) Sinon, IA générale → STREAMING (avec interception JSON)
    await streamChat(userText, tmpUserId);
    sendInFlightRef.current = false;
  };

  const handleKeyDown = useCallback((e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }, [handleSend]);

  /* Menu contextuel : suppression d’un message uniquement */
  const placeContextMenu = useCallback((msg, clientX, clientY) => {
    if (!msg) return;

    const menuWidth = 248;
    const menuHeight = 56;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 768;

    const x = Math.max(10, Math.min(Number(clientX) || 10, viewportWidth - menuWidth - 10));
    const y = Math.max(10, Math.min(Number(clientY) || 10, viewportHeight - menuHeight - 10));

    setContextMenu({ msg, x, y });
  }, []);

  const openMessageContextMenu = useCallback((event, msg) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    placeContextMenu(msg, event?.clientX, event?.clientY);
  }, [placeContextMenu]);

  const requestDeleteMessage = useCallback((msg) => {
    if (!msg) return;
    setContextMenu(null);
    setToDelete(msg);
    setShowDeleteConfirm(true);
  }, []);

  const onPressStart = useCallback((msg, event) => {
    if (!msg) return;

    const touch = event?.touches?.[0] || event?.changedTouches?.[0];
    const clientX = touch?.clientX ?? event?.clientX ?? (typeof window !== "undefined" ? window.innerWidth / 2 : 120);
    const clientY = touch?.clientY ?? event?.clientY ?? (typeof window !== "undefined" ? window.innerHeight / 2 : 120);

    setLongPressId(msg.id);
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      placeContextMenu(msg, clientX, clientY);
      setLongPressId(null);
    }, LONG_PRESS_MS);
  }, [placeContextMenu]);

  const onPressEnd = useCallback(() => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    setLongPressId(null);
  }, []);

  useEffect(() => {
    if (!contextMenu) return undefined;

    const close = () => setContextMenu(null);
    const onKey = (event) => {
      if (event.key === "Escape") close();
    };
    const scroller = scrollRef.current;

    window.addEventListener("click", close);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    scroller?.addEventListener("scroll", close, { passive: true });

    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
      scroller?.removeEventListener("scroll", close);
    };
  }, [contextMenu]);

  const confirmDelete = useCallback(async () => {
    if (!toDelete) return;
    setShowDeleteConfirm(false);
    setContextMenu(null);
    setErrorMessage("");
    setSuccessMessage("");

    if (!toDelete.persistedId) {
      const fingerprint = fp(toDelete);
      removeLocalByFP(fingerprint);
      setMessages((current) => current.filter((m) => m.id !== toDelete.id));
      setToDelete(null);
      setSuccessMessage(t("chat.delete.localSuccess", { defaultValue: "Message supprimé." }));
      setTimeout(() => setSuccessMessage(""), 2500);
      return;
    }

    try {
      const targetId = toDelete.persistedId || toDelete.id;
      const res = await fetchJsonForChat(`/api/chat/${encodeURIComponent(targetId)}`, {
        method: "DELETE",
      });

      if (res?.error) {
        // Une erreur de suppression ne doit JAMAIS déconnecter l'utilisateur.
        // La session globale reste intacte, même si cet endpoint répond 401/403.
        if (res?.status === 401 || res?.status === 403 || /(Non autorisé|expiré|unauthori|forbidden)/i.test(res.error || "")) {
          setErrorMessage(t("chat.delete.sessionError", {
            defaultValue: "Impossible de supprimer ce message pour le moment. Votre session reste active."
          }));
        } else {
          setErrorMessage(res.error || t("chat.delete.error", { defaultValue: "Impossible de supprimer ce message." }));
        }
        return;
      }

      setMessages((current) => current.filter((m) => m.id !== toDelete.id));
      removeLocalByPersistedId(toDelete.persistedId);
      removeLocalByFP(fp(toDelete));
      setToDelete(null);
      setSuccessMessage(t("chat.delete.success", { defaultValue: "Message supprimé définitivement." }));
      setTimeout(() => setSuccessMessage(""), 2500);
    } catch (err) {
      setErrorMessage(t("chat.delete.networkError", {
        message: err.message,
        defaultValue: `Erreur réseau : ${err.message}`,
      }));
    }
  }, [toDelete, t]);

  /* Groupement & dates */
  const grouped = useMemo(() => groupMessagesByDateLocal(messages), [messages]);
  const orderedDates = useMemo(() => Object.keys(grouped).sort((a, b) => {
    const [ay, am, ad] = a.split("-").map(Number); const [by, bm, bd] = b.split("-").map(Number);
    return new Date(ay, am - 1, ad) - new Date(by, bm - 1, bd);
  }), [grouped]);

  /* ---------- UI helpers ---------- */
  const renderOrderDetails = (obj) => {
    if (!obj || typeof obj !== "object") return null;

    const cmd = (obj.action || obj.cmd || obj.command || obj.order_type || obj.kind || "").toString();
    const side = (obj.side || obj.direction || "").toString();
    const symbol = obj.symbol ? keepSymbol(obj.symbol) : "";

    const volume =
      (typeof obj.volume === "number" && Number.isFinite(obj.volume)) ? obj.volume :
        (typeof obj.lot_size === "number" && Number.isFinite(obj.lot_size)) ? obj.lot_size :
          (typeof obj.lot === "number" && Number.isFinite(obj.lot)) ? obj.lot :
            undefined;

    const price = obj.price ?? obj.entry ?? obj.entry_price ?? obj.open_price ?? null;
    const sl = obj.sl ?? obj.stop_loss ?? null;
    const tp = obj.tp ?? obj.take_profit ?? null;

    const cmdLower = String(cmd || "").toLowerCase();

    const items = [];
    if (cmd) items.push([t("chat.order.action"), cmd]);

    // close_all: on n'affiche que l'action (pas de symbole/market/lot)
    if (cmdLower !== "close_all") {
      if (symbol) items.push([t("chat.order.symbol"), symbol]);
      if (side) items.push([t("chat.order.side"), side]);
      if (Number.isFinite(volume)) items.push([t("chat.order.lot"), volume]);
      if (price !== null && price !== undefined && price !== "") items.push([t("chat.order.price"), price]);
      if (sl !== null && sl !== undefined && sl !== "") items.push(["SL", sl]);
      if (tp !== null && tp !== undefined && tp !== "") items.push(["TP", tp]);
    }

    if (!items.length) return null;

    return (
      <div className="mt-2 rounded-2xl border border-white/40 bg-white/55 p-3 text-[14px] text-slate-700 backdrop-blur-xl shadow-[0_10px_40px_rgba(15,18,32,0.08)] dark:border-white/10 dark:bg-white/5 dark:text-white/80">
        <div className="mb-1 font-medium text-slate-700 dark:text-white/90">{t("chat.order.title")}</div>
        <ul className="ml-4 list-disc">
          {items.map(([k, v]) => (
            <li key={k}><span className="font-semibold">{k}</span>: {String(v)}</li>
          ))}
        </ul>
      </div>
    );
  };


  const GLASS_CARD =
    "bg-white border border-slate-200 dark:bg-[#0f1729] dark:border-[#26314a]";
  const GLASS_PILL =
    "bg-white border border-slate-200 dark:bg-[#0f1729] dark:border-[#26314a]";
  const BTN_GLASS =
    "bg-white border border-slate-200 hover:bg-slate-50 dark:bg-[#0f1729] dark:border-[#26314a] dark:hover:bg-[#141d33]";

  // --- Accueil : hero centré + actions rapides ---
  // Raccourcis vers les sections clés — pas d'envoi de message dans le chat.
  // Toujours affichés sous la barre, même en pleine discussion.
  const QUICK_ACTIONS = [
    { label: t("chat.quickActions.agent.label"), icon: Cpu, to: "/agent" },
    { label: t("chat.quickActions.journalAuto.label"), icon: BookOpen, to: "/journal-auto", requiresAccount: true },
    { label: t("chat.quickActions.positions.label"), icon: SlidersHorizontal, to: "/execution" },
    { label: t("chat.quickActions.backtest.label"), icon: PlayCircle, to: "/backtest" },
  ];
  const isWelcomeOnly =
    Array.isArray(messages) &&
    messages.length > 0 &&
    messages.every((m) =>
      [WELCOME_GREETING_TYPE, WELCOME_CONNECTION_TYPE, WELCOME_ACTIONS_TYPE].includes(m.type)
    );
  const showHero = !messages || messages.length === 0 || isWelcomeOnly;
  // Raccourci : navigation directe vers une section (aucun message envoyé).
  // Le journal auto exige un compte trading connecté (Agent IA installé) ;
  // sinon on invite à installer l'agent.
  const runQuickAction = (q) => {
    if (!q || !q.to) return;
    if (q.requiresAccount && !getAgentTokenLocal(getCurrentChatScope())) {
      setErrorMessage(t("chat.quickActions.journalAuto.needAgent"));
      navigate("/agent");
      return;
    }
    navigate(q.to);
  };

  /* ---------- UI ---------- */
  return (
    <div className="relative flex h-[100dvh] min-h-0 flex-col overflow-hidden text-slate-900 antialiased dark:text-white">
      <style>{`
        .scrollbar-hide::-webkit-scrollbar{display:none}
        *, *::before, *::after{ box-sizing:border-box; }
        html, body, #root{
          background:#f6f7fb;
          overscroll-behavior-y:none;
        }
        html.dark, html.dark body, html.dark #root{
          background:#070912;
        }
        .fm-composer{
          position:fixed; inset-inline:0; bottom:0; z-index:20;
          left: var(--fm-c-left, 0px);
          right: var(--fm-c-right, 0px);
          max-width:100%;
          transform: translate3d(0, calc(-1 * var(--fm-vv-offset, 0px)), 0);
          will-change: transform;
          backface-visibility:hidden;
          -webkit-transform: translate3d(0, calc(-1 * var(--fm-vv-offset, 0px)), 0);
          contain: layout paint;
        }
        .fm-composer::before{
          content:"";
          position:absolute;
          left:0;
          right:0;
          bottom:-72px;
          height:calc(72px + max(14px, env(safe-area-inset-bottom)));
          pointer-events:none;
          background:linear-gradient(to top, rgba(246,247,251,0.98), rgba(246,247,251,0.88), rgba(246,247,251,0));
        }
        .dark .fm-composer::before{
          background:linear-gradient(to top, rgba(7,9,18,0.99), rgba(7,9,18,0.90), rgba(7,9,18,0));
        }
        .fm-composer > form{
          position:relative;
          z-index:1;
        }
        .fm-chat-textarea{
          -webkit-text-size-adjust:100%;
          touch-action: manipulation;
          overscroll-behavior: contain;
        }
        .fm-chat-textarea::placeholder{
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .fm-input-frame{
          background: #e2e8f0;
          box-shadow: none;
          transition: background .2s ease;
        }
        .fm-input-frame:focus-within{
          background: #8b5cf6;
          box-shadow: none;
        }
        .fm-input-inner{
          background: #ffffff;
          border: 1px solid rgba(15,18,32,0.08);
          box-shadow: none;
        }
        .fm-input-glow{
          background: none;
          opacity: 0;
        }
        .fm-send-btn{
          background: #7c3aed;
          box-shadow: none;
        }
        .dark .fm-input-frame{
          background: #26314a;
          box-shadow: none;
        }
        .dark .fm-input-inner{
          background: #0f1729;
          border-color: #2a3550;
          box-shadow: none;
        }
        .dark .fm-input-frame:focus-within{
          background: #7c3aed;
        }
        .dark .fm-input-glow{
          background: none;
          opacity: 0;
        }
        .dark .fm-chat-textarea{
          color: #f8fafc;
        }
        .dark .fm-chat-textarea::placeholder{
          color: rgba(203,213,225,0.72);
        }
        .dark .fm-send-btn{
          background: #7c3aed;
          box-shadow: none;
        }
        /* Accueil hero : le composer occupe le centre de l'écran. */
        .fm-composer--hero{
          top: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .fm-composer--hero::before{ display: none; }
        .fm-composer--hero > form{ width: 100%; }
        .fm-composer--hero .fm-input-frame,
        .fm-composer--hero .fm-input-inner{ border-radius: 28px; }
        /* Accueil desktop : barre de saisie mince, comme sur mobile.
           Elle faisait 96px de haut pour une seule ligne de texte. */
        @media (min-width: 640px){
          .fm-composer--hero .fm-chat-textarea{
            min-height: 44px !important;
            padding-top: 10px !important;
            padding-bottom: 10px !important;
            font-size: 16px !important;
          }
        }
        @media (max-width: 639px){
          .fm-composer{
            left: max(var(--fm-c-left, 0px), env(safe-area-inset-left));
            right: max(var(--fm-c-right, 0px), env(safe-area-inset-right));
          }
          .fm-composer > form{
            padding-left: 8px;
            padding-right: 8px;
          }
          .fm-composer-row{
            gap: 7px !important;
          }
          /* Place du « + » réservée seulement quand il est affiché (champ vide).
             Dès qu'on écrit, le texte récupère toute la largeur. */
          .fm-chat-textarea.fm-has-plus{ padding-left:46px !important; }
          .fm-chat-textarea{
            min-height:44px !important;
            padding-left:14px !important;
            padding-right:14px !important;
            padding-top:10px !important;
            padding-bottom:10px !important;
            font-size:16px !important;
            line-height:1.35 !important;
            word-break: normal !important;
            overflow-wrap: break-word !important;
          }
          .fm-send-btn{
            width:42px !important;
            height:42px !important;
          }
          /* Accueil mobile = app native : la salutation prend tout l'espace libre
             et s'y centre, la barre de saisie reste collée en bas. Le hero vit
             dans le <form>, donc sans ça les deux descendaient ensemble et
             laissaient un grand vide au-dessus. */
          .fm-composer--hero{
            top:0 !important;
            bottom:0 !important;
            flex-direction:column !important;
            align-items:stretch !important;
            justify-content:flex-end !important;
          }
          .fm-composer--hero::before{ display:none !important; }
          .fm-composer--hero > form{
            display:flex;
            flex-direction:column;
            width:100%;
            height:100%;
          }
          .fm-composer--hero .fm-hero-greeting{
            flex:1 1 auto;
            display:flex;
            flex-direction:column;
            align-items:center;
            justify-content:center;
            margin-bottom:0;
          }
          /* annule l'input géant du hero desktop */
          .fm-composer--hero .fm-chat-textarea{
            min-height:44px !important;
            padding-top:10px !important;
            padding-bottom:10px !important;
            font-size:16px !important;
          }
          .fm-composer--hero .fm-input-frame,
          .fm-composer--hero .fm-input-inner{ border-radius:24px; }
        }
        @media (max-width: 380px){
          .fm-composer > form{
            padding-left: 6px;
            padding-right: 6px;
          }
          .fm-composer-row{
            gap: 6px !important;
          }
          .fm-chat-textarea.fm-has-plus{ padding-left:44px !important; }
          .fm-chat-textarea{
            min-height:42px !important;
            padding-left:12px !important;
            padding-right:12px !important;
            padding-top:9px !important;
            padding-bottom:9px !important;
          }
          .fm-send-btn{
            width:40px !important;
            height:40px !important;
          }
        }
        .fm-dot{ width:8px; height:8px; border-radius:9999px; background:currentColor; opacity:0.9; }
        .fm-bounce{ animation: fm-bounce 1s infinite; }
        .fm-bounce2{ animation: fm-bounce 1s infinite 0.15s; }
        .fm-bounce3{ animation: fm-bounce 1s infinite 0.30s; }
        @keyframes fm-bounce{
          0%, 80%, 100% { transform: translateY(0); opacity:.5; }
          40% { transform: translateY(-4px); opacity:1; }
        }
        .fm-noise{
          background-image:
            radial-gradient(circle at 18% 12%, rgba(124,58,237,0.18), transparent 40%),
            radial-gradient(circle at 85% 0%, rgba(217,70,239,0.14), transparent 35%),
            radial-gradient(circle at 90% 70%, rgba(99,102,241,0.10), transparent 45%);
        }
        .dark .fm-noise{
          background-image:
            radial-gradient(circle at 18% 12%, rgba(124,58,237,0.22), transparent 45%),
            radial-gradient(circle at 85% 0%, rgba(217,70,239,0.18), transparent 40%),
            radial-gradient(circle at 90% 70%, rgba(99,102,241,0.14), transparent 50%);
        }
      `}</style>

      {/* Couche “glace 2026” — desktop uniquement.
          Sur mobile le fond reste un aplat unique (celui de body), façon app native. */}
      <div className="pointer-events-none absolute inset-0 hidden sm:block">
        <div className="absolute inset-0 fm-noise opacity-100" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(246,247,251,0.55),rgba(246,247,251,0.95))] dark:bg-[linear-gradient(to_bottom,rgba(7,9,18,0.18),rgba(7,9,18,0.98))]" />
      </div>

      {/* Messages */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        <div ref={containerRef} className="relative h-full w-full max-w-[56rem] mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex h-full flex-col overflow-hidden pt-3 sm:pt-5">

            {errorMessage && (
              <div
                style={{ left: "calc((var(--fm-c-left, 0px) + 100vw - var(--fm-c-right, 0px)) / 2)" }}
                className={cn(
                "fixed top-[68px] sm:top-4 -translate-x-1/2 z-40 flex w-[min(90%,42rem)] items-center justify-between rounded-2xl px-3.5 py-2.5 text-sm text-amber-800",
                "bg-amber-50/90 border border-amber-200/70 backdrop-blur-xl shadow-[0_8px_30px_rgba(15,18,32,0.10)]",
                "dark:bg-amber-500/10 dark:text-amber-100 dark:border-amber-400/20"
              )}>
                <span>{errorMessage}</span>
                <button onClick={() => setErrorMessage("")} className="rounded-xl p-1 hover:bg-black/5 dark:hover:bg-white/10">
                  <XCircle size={18} />
                </button>
              </div>
            )}

            {successMessage && (
              <div
                style={{ left: "calc((var(--fm-c-left, 0px) + 100vw - var(--fm-c-right, 0px)) / 2)" }}
                className={cn(
                "fixed top-[68px] sm:top-4 -translate-x-1/2 z-40 flex w-[min(90%,42rem)] items-center justify-between rounded-2xl px-3.5 py-2.5 text-sm text-emerald-800",
                "bg-emerald-50/90 border border-emerald-200/70 backdrop-blur-xl shadow-[0_8px_30px_rgba(15,18,32,0.10)]",
                "dark:bg-emerald-500/10 dark:text-emerald-100 dark:border-emerald-400/25"
              )}>
                <span>{successMessage}</span>
                <button onClick={() => setSuccessMessage("")} className="rounded-xl p-1 hover:bg-black/5 dark:hover:bg-white/10">
                  <CheckCircle size={18} />
                </button>
              </div>
            )}

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto scrollbar-hide"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none", paddingBottom: "calc(var(--fm-composer-h, 88px) + max(22px, env(safe-area-inset-bottom)))" }}
            >
              <div className="pb-2">
                {/* Accueil (greeting + chips) déplacé dans le composer centré ci-dessous. */}
                {!showHero && orderedDates.map(dateKey => (
                  <div key={dateKey} className="mx-auto mb-5 max-w-[56rem]">
                    <div className="mb-4 flex justify-center">
                      <span className={cn(
                        "rounded-full px-3 py-1 text-[11px] tracking-wide",
                        "text-slate-700/80 dark:text-white/70",
                        GLASS_PILL
                      )}>
                        {formatDateChipFR(dateKey, t)}
                      </span>
                    </div>


{grouped[dateKey].map(msg => {
  if (msg.type === WELCOME_GREETING_TYPE) {
    return (
      <div key={msg.id} className="mb-2 flex w-full items-start justify-start">
        <WelcomeGreetingCard
          meta={msg.meta || {}}
          glassClass={GLASS_CARD}
        />
      </div>
    );
  }

  if (msg.type === WELCOME_CONNECTION_TYPE) {
    return (
      <div key={msg.id} className="mb-2 flex w-full items-start justify-start">
        <WelcomeConnectionCard
          meta={msg.meta || {}}
          glassClass={GLASS_CARD}
        />
      </div>
    );
  }

  if (msg.type === WELCOME_ACTIONS_TYPE) {
    return (
      <div key={msg.id} className="mb-2 flex w-full items-start justify-start">
        <WelcomeActionsCard
          meta={msg.meta || {}}
          glassClass={GLASS_CARD}
          onOpenAgent={() => navigate((msg.meta && msg.meta.ctaTarget) || "/agent")}
        />
      </div>
    );
  }

                      // CTA
                      if (msg.type === "cta" && (msg.cta || msg.meta?.cta)) {
                        const cta = msg.cta || msg.meta?.cta;
                        return (
                          <div
                            key={msg.id}
                            className="mb-2 flex w-full items-end gap-2 justify-start"
                            onContextMenu={(event) => openMessageContextMenu(event, msg)}
                            onTouchStart={(event) => onPressStart(msg, event)}
                            onTouchEnd={onPressEnd}
                            onTouchCancel={onPressEnd}
                          >
                            <div className={cn(
                              "max-w-[min(100%,42rem)] rounded-[24px] rounded-bl-lg px-3 py-3 text-[15px] leading-7",
                              "text-slate-900 dark:text-slate-50",
                              GLASS_CARD
                            )}>
                              <div className="flex items-start gap-3">
                                <div className={cn(
                                  "mt-1 grid h-9 w-9 place-items-center rounded-2xl",
                                  "bg-slate-100 border border-slate-200",
                                  "dark:bg-white/7 dark:border-white/10"
                                )}>
                                  <Bot className="opacity-80" size={18} />
                                </div>

                                <div className="min-w-0 flex-1">
                                  <div className="mb-1 font-semibold text-slate-900 dark:text-white/90">
                                    {cta.title || t("chat.cta.open")}
                                  </div>
                                  <div className="mb-3 text-[14px] text-slate-700/80 dark:text-white/70">
                                    {msg.text || msg.meta?.reason || t("chat.cta.openRequestedPage")}
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      onClick={() => navigate(cta.target || "/")}
                                      className={cn(
                                        "relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white",
                                        "bg-violet-600",
                                        "hover:bg-violet-700 active:scale-95"
                                      )}
                                    >
                                      <span className="relative inline-flex items-center gap-2">
                                        <PlugZap size={16} /> {cta.buttonLabel || t("chat.cta.open")} <ArrowRight size={16} />
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Reçu d’ordre
                      if (msg.type === "order_receipt") {
                        return (
                          <div
                            key={msg.id}
                            className="mb-2 flex w-full items-end gap-2 justify-start"
                            onContextMenu={(event) => openMessageContextMenu(event, msg)}
                            onTouchStart={(event) => onPressStart(msg, event)}
                            onTouchEnd={onPressEnd}
                            onTouchCancel={onPressEnd}
                          >
                            <div className={cn(
                              "max-w-[min(100%,42rem)] rounded-[24px] rounded-bl-lg px-3 py-3 text-[15px] leading-7",
                              "text-slate-900 dark:text-slate-50",
                              GLASS_CARD
                            )}>
                              <div className="flex items-center gap-2 font-semibold text-emerald-700 dark:text-emerald-300">
                                <CheckCircle size={18} /> {msg.text || t("chat.trade.orderSent")}
                              </div>
                              {renderOrderDetails(msg.meta?.payload)}
                              {Array.isArray(msg.meta?.tickets) && msg.meta.tickets.length > 0 && (
                                <div className="mt-2 text-[13px] text-slate-700/80 dark:text-slate-200/90">
                                  Tickets: {msg.meta.tickets.join(", ")}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }

                      // Standard
                      const isUser = msg.author === "user";
                      const bubbleBase =
                        "text-[15px] leading-7 break-words whitespace-pre-wrap";
                      const bubbleUser = cn(
                        "max-w-[min(78%,26rem)] rounded-[24px] rounded-br-lg px-3 py-2 text-white",
                        "bg-violet-600",
                        "relative overflow-hidden"
                      );
                      // Mobile = texte brut (façon ChatGPT), pas de carte. Desktop = carte.
                      const bubbleAI = cn(
                        "max-w-full text-slate-900 dark:text-slate-50",
                        "bg-transparent px-0 py-0",
                        "sm:max-w-[42rem] sm:rounded-[24px] sm:rounded-bl-lg sm:px-3 sm:py-2 sm:bg-white sm:border sm:border-slate-200 sm:dark:bg-[#0f1729] sm:dark:border-[#26314a]",
                        "relative overflow-hidden"
                      );

                      return (
                        <div
                          key={msg.id}
                          className={cn("mb-2 flex w-full items-end gap-2", isUser ? "justify-end" : "justify-start")}
                          onContextMenu={(event) => openMessageContextMenu(event, msg)}
                          onTouchStart={(event) => onPressStart(msg, event)}
                          onTouchEnd={onPressEnd}
                          onTouchCancel={onPressEnd}
                        >
                          <div
                            className={cn(
                              bubbleBase,
                              isUser ? bubbleUser : bubbleAI,
                              longPressId === msg.id ? `ring-2 ${BRAND.ring}` : "",
                            )}
                            style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                          >
                            {/* gloss overlay */}
                            <span
                              className={cn(
                                "pointer-events-none absolute inset-0",
                                isUser
                                  ? "opacity-100 bg-[radial-gradient(circle_at_30%_10%,rgba(255,255,255,0.28),transparent_55%)]"
                                  : "hidden sm:block opacity-90 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.38),transparent_55%)] dark:opacity-25"
                              )}
                            />
                            <div className="relative">
                              {msg.author === "ai"
                                ? <MarkdownRenderer content={msg.text} />
                                : msg.text.split("\n").map((line, i) => (<React.Fragment key={i}>{line}<br /></React.Fragment>))
                              }
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}

                {/* Bulle de streaming / typing */}
                {loading && (
                  <div className="mx-auto mb-2 w-full max-w-[56rem]">
                    <div className="flex w-full items-end gap-2 justify-start">
                      <div
                        className={cn(
                          "max-w-full text-[15px] leading-7 break-words whitespace-pre-wrap",
                          "text-slate-900/90 dark:text-white/90",
                          "bg-transparent px-0 py-0",
                          "sm:max-w-[42rem] sm:rounded-[24px] sm:rounded-bl-lg sm:px-3 sm:py-2 sm:bg-white sm:border sm:border-slate-200 sm:dark:bg-[#0f1729] sm:dark:border-[#26314a]",
                          "relative overflow-hidden"
                        )}
                        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                      >
                        <span className="pointer-events-none absolute inset-0 hidden sm:block opacity-90 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.38),transparent_55%)] dark:opacity-25" />
                        <div className="relative">
                          {typingText
                            ? <MarkdownRenderer content={typingText} />
                            : <div className="flex items-center gap-2 text-slate-600/80 dark:text-slate-200/85">
                              <span className="fm-dot fm-bounce" />
                              <span className="fm-dot fm-bounce2" />
                              <span className="fm-dot fm-bounce3" />
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            {showDownBtn && (
              <button
                onClick={() => scrollToBottom()}
                title={t("chat.scrollDown")}
                aria-label={t("chat.scrollDown")}
                className={cn(
                  "absolute right-3 sm:right-5 z-20 grid h-10 w-10 place-items-center rounded-full",
                  "text-violet-700 dark:text-violet-200",
                  BTN_GLASS,
                  "transition active:scale-95"
                )}
                style={{ bottom: "calc(var(--fm-composer-h, 88px) + 10px)" }}
              >
                <ChevronDown size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Composer */}
      <div className={cn("pointer-events-none fm-composer", showHero && "fm-composer--hero")}>
        <form
          ref={composerRef}
          onSubmit={handleSend}
          className={cn(
            "pointer-events-auto w-full max-w-[56rem] mx-auto px-2 sm:px-5 will-change-transform",
            "pt-1.5 pb-[max(6px,env(safe-area-inset-bottom))] sm:pt-2 sm:pb-[max(10px,env(safe-area-inset-bottom))]"
          )}
        >
          {/* Accueil hero : greeting au-dessus de la barre */}
          {showHero && (
            <div className="fm-hero-greeting order-1 pointer-events-none mb-5 text-center pr-0 sm:mb-6 sm:pr-[58px]">
              <div className="relative mb-2 flex flex-col items-center gap-1.5 sm:mb-3 sm:inline-flex sm:flex-row sm:gap-3">
                <Sparkles
                  className="text-violet-500 dark:text-violet-300"
                  size={30}
                />
                <h1 className="bg-gradient-to-r from-slate-900 via-violet-700 to-fuchsia-600 bg-clip-text text-[1.5rem] font-bold leading-tight tracking-tight text-transparent [text-wrap:balance] dark:from-white dark:via-violet-200 dark:to-fuchsia-300 sm:text-[2.6rem]">
                  {(() => {
                    const { greeting, who } = heroGreetingParts(viewerName, new Date(), t);
                    if (!who) return greeting;
                    return (
                      <>
                        {greeting},{" "}
                        {/* Mobile : le prénom passe à la ligne. Desktop : tout sur une ligne. */}
                        <br className="sm:hidden" />
                        {who}
                      </>
                    );
                  })()}
                </h1>
              </div>
              <p className="mx-auto hidden max-w-md text-[15px] leading-relaxed text-slate-500 dark:text-slate-400 sm:block">
                {t("chat.hero.subtitle")}
              </p>
            </div>
          )}

          <div className="fm-composer-row order-3 flex items-end gap-2 sm:gap-3">
            {/* INPUT */}
            <div className="min-w-0 flex-1">
              {(() => {
                const pill = "rounded-[999px]";
                const card = "rounded-[28px]";
                const shellRadius = isMultiline ? card : pill;

                return (
                  <div
                    className={cn(
                      shellRadius,
                      "fm-input-frame p-[1.5px] transition-colors duration-150"
                    )}
                  >
                    <div
                      className={cn(
                        shellRadius,
                        "fm-input-inner relative overflow-hidden backdrop-blur-xl transition-colors duration-150"
                      )}
                    >
                      <span className="pointer-events-none absolute inset-0 fm-input-glow" />

                      {/* Mobile : « + » ouvre les actions rapides (elles ne sont pas
                          affichées en dur sous la barre comme sur desktop).
                          Disparaît dès qu'on écrit, pour rendre la place au texte.
                          Calé en bas pour rester en place quand le champ grandit. */}
                      {!canSend && (
                        <button
                          type="button"
                          onClick={() => setPresetsOpen(true)}
                          aria-label={t("chat.quickActionsLabel")}
                          className="fm-tap absolute bottom-[5px] left-1.5 z-10 grid h-9 w-9 place-items-center rounded-full text-slate-500 dark:text-slate-300 sm:hidden"
                        >
                          <Plus size={20} />
                        </button>
                      )}

                      <textarea
                        ref={textareaRef}
                        rows={1}
                        maxLength={20000}
                        defaultValue={input}
                        onInput={handleComposerInput}
                        onKeyDown={handleKeyDown}
                        placeholder={compactComposerPlaceholder ? t("chat.composer.placeholderCompact") : t("chat.composer.placeholder")}
                        aria-label={t("chat.composer.ariaLabel")}
                        disabled={loading}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="sentences"
                        inputMode="text"
                        enterKeyHint="send"
                        className={cn(
                          "fm-chat-textarea relative block w-full min-h-[46px] max-h-[178px] sm:max-h-[320px] resize-none",
                          // Réserve la place du « + » tant qu'il est affiché (mobile, champ vide).
                          !canSend && "fm-has-plus",
                          shellRadius,
                          "bg-transparent px-4 py-2.5 text-[16px] leading-[1.45] outline-none sm:text-[14px] sm:leading-normal",
                          "text-slate-950 placeholder:text-slate-500/75 dark:text-slate-50 dark:placeholder:text-slate-300",
                          "caret-violet-500 selection:bg-violet-500/30 dark:caret-violet-300",
                          "disabled:opacity-70 disabled:cursor-not-allowed"
                        )}
                        onFocus={() => {
                          window.setTimeout(() => {
                            if (atBottomRef.current) ensureBottom();
                          }, 90);
                        }}
                        style={{
                          overflowY: "hidden",
                          wordBreak: "normal",
                          overflowWrap: "break-word",
                          WebkitOverflowScrolling: "touch",
                        }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* SEND */}
            <button
              type="submit"
              disabled={!canSend || loading}
              className={cn(
                "fm-send-btn relative self-end grid h-11 w-11 sm:h-[46px] sm:w-[46px] shrink-0 place-items-center rounded-full text-white",
                "transition-transform duration-150 active:scale-95",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
              aria-label={t("chat.composer.send")}
            >
              <span className="relative">
                <Send size={18} />
              </span>
            </button>
          </div>

          {/* Raccourcis de sections — desktop, TOUJOURS sous la barre (accueil
              comme en pleine discussion). Ils naviguent, sans écrire dans le chat.
              Sur mobile ils restent accessibles via le « + » de la barre. */}
          <div className="order-2 hidden sm:mt-5 sm:mb-0 sm:flex sm:flex-row sm:flex-wrap sm:justify-center sm:gap-3 sm:pr-[58px]">
            {QUICK_ACTIONS.map((q) => {
              const QIcon = q.icon;
              return (
                <button
                  key={q.label}
                  type="button"
                  onClick={() => runQuickAction(q)}
                  className="pointer-events-auto group flex items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-left text-sm font-semibold text-slate-700 transition-colors duration-150 hover:border-violet-300 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:border-violet-400/40 dark:hover:bg-white/10"
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-violet-100 text-violet-600 transition-colors duration-200 group-hover:bg-violet-500 group-hover:text-white dark:bg-violet-500/15 dark:text-violet-300">
                    <QIcon size={16} />
                  </span>
                  {q.label}
                </button>
              );
            })}
          </div>

          {!showHero && (
            <div className="hidden sm:block pointer-events-none pt-2">
              <p className="text-center text-[11px] leading-5 text-slate-700/75 dark:text-slate-300 select-none">
                {t("chat.hint")}
              </p>
            </div>
          )}
        </form>
      </div>

      {/* Actions rapides (mobile) : feuille du bas ouverte par le « + » de la barre. */}
      {presetsOpen && (
        <div className="fixed inset-0 z-[70] sm:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setPresetsOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 animate-fadeIn rounded-t-3xl bg-white pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl dark:bg-[#181a2e]">
            <div className="flex justify-center pt-2.5 pb-1">
              <span className="h-1.5 w-10 rounded-full bg-slate-300 dark:bg-white/20" />
            </div>
            <div className="p-2">
              {QUICK_ACTIONS.map((q) => {
                const QIcon = q.icon;
                return (
                  <button
                    key={q.label}
                    type="button"
                    onClick={() => {
                      runQuickAction(q);
                      setPresetsOpen(false);
                    }}
                    className="fm-tap flex w-full items-center gap-3.5 rounded-2xl px-4 py-3.5 text-left"
                  >
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
                      <QIcon size={18} />
                    </span>
                    <span className="text-[15px] font-medium text-slate-700 dark:text-slate-100">
                      {q.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Menu clic droit / appui long sur un message */}
      {contextMenu?.msg && (
        <div
          className="fixed z-[90] w-[248px] overflow-hidden rounded-2xl border border-slate-200 bg-white/98 p-1.5 shadow-[0_18px_60px_rgba(15,23,42,0.22)] backdrop-blur-xl dark:border-white/10 dark:bg-[#111827]/98"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => requestDeleteMessage(contextMenu.msg)}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-rose-700 transition hover:bg-rose-50 dark:text-rose-200 dark:hover:bg-rose-500/10"
          >
            <Trash2 size={16} />
            {t("chat.delete.contextAction", { defaultValue: "Supprimer ce message" })}
          </button>
        </div>
      )}

      {/* Modale suppression */}
      {showDeleteConfirm && toDelete && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/55 backdrop-blur-sm">
          <div className={cn(
            "w-[92vw] max-w-md rounded-[26px] p-5",
            "text-slate-900 dark:text-white",
            "backdrop-blur-xl bg-white/70 border border-white/45",
            "shadow-[0_22px_90px_rgba(0,0,0,0.35)]",
            "dark:bg-[#111827]/95 dark:border-white/15"
          )}>
            <h3 className="mb-2 text-lg font-semibold">{t("chat.delete.confirmTitle")}</h3>
            <p className="text-sm text-slate-700/80 dark:text-white/70">
              {t("chat.delete.confirmText")}
            </p>

            <div className={cn(
              "mt-3 max-h-40 overflow-y-auto rounded-2xl p-3 text-sm",
              "bg-white/55 border border-white/40 text-slate-800/90",
              "dark:bg-[#111827]/80 dark:border-white/15 dark:text-white/80"
            )}>
              "{String(toDelete.text || "").substring(0, 200)}{String(toDelete.text || "").length > 200 ? "…" : ""}"
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                className={cn(
                  "rounded-xl px-3 py-2 text-sm font-semibold",
                  "text-slate-800",
                  "bg-white/55 border border-white/40 backdrop-blur-xl",
                  "hover:bg-white/75",
                  "dark:text-white/80 dark:bg-white/6 dark:border-white/10 dark:hover:bg-white/10"
                )}
                onClick={() => { setShowDeleteConfirm(false); setToDelete(null); }}
              >
                {t("chat.delete.cancel")}
              </button>
              <button
                className={cn(
                  "rounded-xl px-3 py-2 text-sm font-semibold text-white",
                  "bg-red-500/90 border border-red-300/60 shadow-[0_14px_55px_rgba(239,68,68,0.25)]",
                  "hover:bg-red-600 active:scale-[0.98]",
                  "dark:bg-red-500/25 dark:border-red-400/25 dark:hover:bg-red-500/30"
                )}
                onClick={confirmDelete}
              >
                {t("chat.delete.deleteButton")}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}